/**
 * User Page
 * 
 * 用户管理页面逻辑
 */

import { UserManager } from '../core/UserManager';
import { AccountManager } from '../core/AccountManager';
import { TradingSettingsManager } from '../core/TradingSettingsManager';
import { BrokerSettingsManager } from '../core/BrokerSettingsManager';
import { TRADING_SETTINGS_HELP, DEFAULT_TRADING_SETTINGS } from '../types/tradingSettings.types';
import type { TradingSettings } from '../types/tradingSettings.types';
import { logger } from '../utils/logger';
import { getDepositFeeDetails, formatFeeRate } from '../utils/feeCalculator';
import { accountApi } from '../services/AccountApiService';
import type { AccountInfo } from '../services/AccountApiService';
import { settingsApi } from '../services/SettingsApiService';
import { referralApi } from '../services/ReferralApiService';
import { authApi } from '../services/AuthApiService';

// ✅ API 基础 URL
const API_BASE = '';

class UserPage {
  private userManager: UserManager;
  private accountManager: AccountManager;
  private tradingSettingsManager: TradingSettingsManager;
  private brokerSettingsManager: BrokerSettingsManager;
  private referralEventsAttached: boolean = false; // 防止重复绑定
  private currentReferralCodes: any[] = []; // 存储推荐码列表
  private currentCodeForOperation: any = null; // 当前操作的推荐码

  constructor() {
    // 初始化管理器
    this.accountManager = new AccountManager();
    this.userManager = new UserManager(this.accountManager);
    this.tradingSettingsManager = new TradingSettingsManager();
    this.brokerSettingsManager = new BrokerSettingsManager();

    // 设置 AccountManager 依赖（用于出入金功能）
    this.accountManager.setDependencies(
      this.userManager,
      this.tradingSettingsManager,
      this.brokerSettingsManager
    );

    // 检查权限
    this.checkPermission();

    // 渲染UI
    this.render();

    logger.log('User page initialized');
  }

  /**
   * 检查权限
   */
  private checkPermission(): void {
    const session = this.userManager.getCurrentSession();
    
    if (!session) {
      alert('请先登录');
      window.location.href = '../pages/unified-login.html';
      return;
    }
  }

  /**
   * 渲染UI
   */
  private async render(): Promise<void> {
    let currentUser = this.userManager.getCurrentUser();
    if (!currentUser) {
      alert('无法获取用户信息');
      window.location.href = '../pages/unified-login.html';
      return;
    }

    // ✅ 检查 token 是否存在（解决 V3 统一端口架构的时序问题）
    const token = authApi.getToken();
    if (!token) {
      logger.warn('⏳ Token 暂时不可用，等待 100ms 后重试...');
      // 等待一小段时间，让 ApiClient 有机会从 current_session 恢复 token
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const retryToken = authApi.getToken();
      if (!retryToken) {
        logger.error('❌ Token 仍然不可用，跳转登录页');
        alert('无法获取认证信息，请重新登录');
        window.location.href = '../pages/unified-login.html';
        return;
      }
    }

    // ✅ 从后端API获取最新用户信息（包括role、推荐码等所有字段）
    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${authApi.getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`API返回错误: ${response.status}`);
      }
      
      const data = await response.json();
      if (!data.user) {
        throw new Error('API未返回用户数据');
      }
      
      // ✅ 使用后端返回的完整用户数据，包括role
      currentUser = {
        uid: data.user.id,
        username: data.user.username,
        displayName: data.user.displayName || data.user.username,
        email: data.user.email || '',
        role: data.user.role, // ✅ 从后端获取最新role
        status: data.user.status || 'active',
        accountId: data.user.id,
        referralCode: data.user.referralCode,
        customReferralCode: data.user.customReferralCode,
        createdAt: new Date(data.user.createdAt).getTime(), // ✅ 转换为时间戳
        lastLoginAt: Date.now(),
        walletAddress: data.user.walletAddress || null, // ✅ MetaMask钱包地址
        googleId: data.user.googleId || null, // ✅ Google ID
        referrerInfo: data.user.referrerInfo || null, // ✅ 推荐人信息
        usedReferralCode: data.user.usedReferralCode || null, // ✅ 使用的推荐码
      };
      
      // ✅ 同步更新localStorage中的用户数据
      const users = JSON.parse(localStorage.getItem('users') || '{}');
      users[currentUser.uid] = currentUser;
      localStorage.setItem('users', JSON.stringify(users));
      
      logger.info('✅ 已从后端API获取最新用户信息（包括role）:', currentUser);
    } catch (error) {
      // ❌ 移除降级措施：如果后端API失败，应该提示用户重新登录，而不是使用过期的本地数据
      logger.error('❌ 获取用户信息失败:', error);
      alert('无法获取最新用户信息，请重新登录');
      authApi.clearSession();
      window.location.href = '../pages/unified-login.html';
      return;
    }

    // 从后端API获取账户信息
    const account = await accountApi.getAccount();
    
    if (!account) {
      // 如果后端没有账户，尝试从localStorage获取
      logger.warn('无法从后端获取账户，尝试使用本地数据');
      try {
        const userWithAccount = this.userManager.getUserWithAccount(currentUser.uid);
        this.renderUserInfo(userWithAccount);
        await this.renderAccountInfo(userWithAccount.account);
      } catch (error) {
        // 显示错误信息
        alert('无法加载账户信息，请联系管理员');
        logger.error('加载账户失败:', error);
        return;
      }
    } else {
      // 使用后端账户数据
      const userWithAccount = {
        ...currentUser,
        account: {
          balance: account.balance,
          availableBalance: account.availableBalance,
          frozenMargin: account.frozenMargin,
          totalDeposit: account.totalDeposit,
          totalWithdrawal: account.totalWithdrawal,
          totalPnl: account.totalPnl,
          totalFee: account.totalFee,
          totalTrades: account.totalTrades,
          winningTrades: account.winningTrades,
          // 添加缺失的字段
          unrealizedPnl: 0,
          realizedPnl: account.totalPnl,
          equity: account.balance,
          marginLevel: 0,
          makerFeeRate: 0.0002,
          takerFeeRate: 0.0004,
        }
      };
      
      this.renderUserInfo(userWithAccount);
      await this.renderAccountInfo(userWithAccount.account);
    }
    
    // 渲染交易设置
    this.renderTradingSettings(currentUser.uid);
    
    // 渲染存取款报表
    this.renderDepositWithdrawalReport();
    
    // 渲染推荐系统
    this.renderReferralSystem();
    
    // 绑定事件
    this.attachDepositWithdrawEvents();
    this.attachReferralEvents();
  }

  /**
   * 渲染用户信息
   */
  private renderUserInfo(user: any): void {
    const container = document.getElementById('user-info');
    if (!container) return;

    // 计算注册后的时间（小时）
    const hoursAfterRegistration = (Date.now() - user.createdAt) / (1000 * 60 * 60);
    const canEditReferrer = hoursAfterRegistration <= 48;

    container.innerHTML = `
      <div class="info-grid">
        <div class="info-item">
          <label>用户名</label>
          <div style="display: flex; gap: 8px; align-items: center;">
            <input 
              type="text" 
              id="username-input" 
              value="${user.username}" 
              style="flex: 1; padding: 6px 10px; border: 1px solid #30363d; border-radius: 6px; background: #0d1117; color: #d1d4dc; font-size: 13px;"
            />
            <button 
              id="save-username-btn"
              style="padding: 6px 16px; background: #10a37f; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; white-space: nowrap;"
            >
              修改
            </button>
          </div>
        </div>
        <div class="info-item">
          <label>邮箱</label>
          <span>${user.email || '未绑定'}</span>
        </div>
        <div class="info-item">
          <label>角色</label>
          <div style="display: flex; gap: 8px; align-items: center;">
            <select 
              id="user-role-select"
              style="flex: 1; padding: 6px 10px; border: 1px solid #30363d; border-radius: 6px; background: #0d1117; color: #d1d4dc; font-size: 13px; cursor: pointer;"
            >
              <option value="trader" ${user.role === 'trader' ? 'selected' : ''}>交易者</option>
              <option value="broker" ${user.role === 'broker' ? 'selected' : ''}>交易商</option>
            </select>
            <button 
              id="save-role-btn"
              style="padding: 6px 16px; background: #10a37f; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; white-space: nowrap;"
            >
              保存
            </button>
          </div>
        </div>
        <div class="info-item" style="grid-column: span 2;">
          <label>Web3地址 (MetaMask)</label>
          <span style="font-size: 12px; word-break: break-all; color: ${user.walletAddress ? '#10a37f' : '#8b949e'};">
            ${user.walletAddress || '未绑定'}
          </span>
        </div>
        <div class="info-item">
          <label>Google账号</label>
          <span style="color: ${user.googleId ? '#10a37f' : '#8b949e'};">${user.email && user.googleId ? user.email : '未绑定'}</span>
        </div>
        <div class="info-item">
          <label>注册时间</label>
          <span>${new Date(user.createdAt).toLocaleString('zh-CN')}</span>
        </div>
        <div class="info-item" style="grid-column: span 2;">
          <label>
            推荐人代码
            ${canEditReferrer 
              ? '<span style="color: #10a37f; font-size: 11px; margin-left: 8px;">✓ 48小时内可编辑</span>' 
              : '<span style="color: #8b949e; font-size: 11px; margin-left: 8px;">⏱ 已超过48小时</span>'
            }
          </label>
          <div style="display: flex; gap: 8px; align-items: center;">
            ${canEditReferrer 
              ? `
                <input 
                  type="text" 
                  id="referrer-id-input" 
                  value="${user.usedReferralCode || ''}" 
                  placeholder="输入推荐码或UID"
                  style="flex: 1; padding: 6px 10px; border: 1px solid #30363d; border-radius: 6px; background: #0d1117; color: #d1d4dc; font-size: 13px;"
                />
                <button 
                  id="save-referrer-btn"
                  style="padding: 6px 16px; background: #10a37f; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; white-space: nowrap;"
                >
                  保存
                </button>
              `
              : `
                <span>${user.usedReferralCode || '无'}</span>
                ${user.usedReferralCode 
                  ? `<button 
                      id="clear-referrer-btn"
                      style="padding: 4px 12px; background: #f85149; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 12px; margin-left: 8px;"
                    >
                      解除
                    </button>`
                  : ''
                }
              `
            }
          </div>
        </div>
        ${user.referrerInfo ? `
          <div class="info-item">
            <label>推荐人</label>
            <span style="color: #10a37f;">
              ${user.referrerInfo.displayName || user.referrerInfo.username}
            </span>
          </div>
          <div class="info-item">
            <label>使用的推荐码</label>
            <span style="color: #10a37f; font-weight: 600;">
              ${user.usedReferralCode || '未知'}
            </span>
          </div>

        ` : ''}
      </div>
    `;

    // 绑定保存用户名事件
    const saveUsernameBtn = document.getElementById('save-username-btn');
    if (saveUsernameBtn) {
      saveUsernameBtn.addEventListener('click', async () => {
        const usernameInput = document.getElementById('username-input') as HTMLInputElement;
        if (!usernameInput) return;

        const newUsername = usernameInput.value.trim();
        if (!newUsername) {
          alert('用户名不能为空');
          return;
        }

        if (newUsername === user.username) {
          alert('用户名未更改');
          return;
        }

        if (newUsername.length < 3 || newUsername.length > 20) {
          alert('用户名长度必须在3-20个字符之间');
          return;
        }

        if (!/^[a-zA-Z0-9_]+$/.test(newUsername)) {
          alert('用户名只能包含字母、数字和下划线');
          return;
        }

        if (!confirm(`确认将用户名修改为 "${newUsername}"？\n\n修改后需要重新登录。`)) {
          return;
        }

        try {
          saveUsernameBtn.textContent = '修改中...';
          saveUsernameBtn.setAttribute('disabled', 'true');

          const result = await authApi.updateUsername(newUsername);

          alert(`✅ ${result.message}\n\n即将跳转到登录页面...`);

          // 清除Session并跳转到统一登录页
          authApi.clearSession();
          setTimeout(() => {
            window.location.href = '../pages/unified-login.html';
          }, 1000);
        } catch (error: any) {
          alert(`❌ 修改用户名失败：${error.message}`);
          saveUsernameBtn.textContent = '修改';
          saveUsernameBtn.removeAttribute('disabled');
        }
      });
    }

    // 绑定其他事件
    this.bindUserInfoEvents(user, canEditReferrer);
  }

  /**
   * 绑定用户信息相关事件
   */
  private bindUserInfoEvents(user: any, canEditReferrer: boolean): void {
    // 保存角色
    const saveRoleBtn = document.getElementById('save-role-btn');
    if (saveRoleBtn) {
      saveRoleBtn.addEventListener('click', async () => {
        const select = document.getElementById('user-role-select') as HTMLSelectElement;
        if (select) {
          const newRole = select.value as 'trader' | 'broker' | 'admin';
          if (newRole !== user.role) {
            if (confirm(`确定要将角色从"${this.getRoleText(user.role)}"切换到"${this.getRoleText(newRole)}"吗？\n\n⚠️ 角色更新后需要重新登录才能生效。`)) {
              try {
                // ✅ 调用后端API更新角色
                await authApi.updateRole(newRole as 'trader' | 'broker' | 'admin');
                
                alert(`✅ 角色已更新为：${this.getRoleText(newRole)}\n\n即将跳转到登录页面，请重新登录。`);
                logger.log(`用户角色已更新: ${user.role} -> ${newRole}`);
                
                // ✅ 清除session并跳转到登录页
                localStorage.removeItem('sim_trading_session');
                setTimeout(() => {
                  window.location.href = '/index.html';
                }, 1000);
              } catch (error: any) {
                alert(`❌ 角色更新失败: ${error.message}`);
                logger.error('角色更新失败:', error);
              }
            }
          } else {
            alert('角色未改变');
          }
        }
      });
    }

    // 保存推荐人代码
    if (canEditReferrer) {
      const saveBtn = document.getElementById('save-referrer-btn');
      if (saveBtn) {
        saveBtn.addEventListener('click', async () => {
          const input = document.getElementById('referrer-id-input') as HTMLInputElement;
          if (input) {
            const referrerCode = input.value.trim();
            
            if (!referrerCode) {
              alert('❌ 请输入推荐码或UID');
              return;
            }

            try {
              saveBtn.textContent = '保存中...';
              saveBtn.setAttribute('disabled', 'true');

              // ✅ 使用后端API设置推荐人
              const response = await fetch('/api/referral/set-referrer', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${authApi.getToken()}`
                },
                body: JSON.stringify({ referrerCode })
              });

              const data = await response.json();

              if (!response.ok) {
                throw new Error(data.error || '设置推荐人失败');
              }

              const action = user.referrerInfo ? '修改' : '保存';
              alert(`✅ 推荐人已${action}！\n您现在可以享受充值费率优惠（9折）！`);
              logger.log(`${action}推荐人代码: ${referrerCode}`);
              
              // 重新渲染页面
              await this.render();
            } catch (error: any) {
              alert(`❌ ${error.message}`);
              logger.error('设置推荐人失败:', error);
            } finally {
              saveBtn.textContent = '保存';
              saveBtn.removeAttribute('disabled');
            }
          }
        });
      }
    } else {
      // 解除推荐人代码
      const clearBtn = document.getElementById('clear-referrer-btn');
      if (clearBtn) {
        clearBtn.addEventListener('click', async () => {
          if (confirm('⚠️ 确定要解除推荐人关系吗？\n\n解除后将失去充值费率优惠。\n此操作不可撤销。')) {
            try {
              clearBtn.textContent = '处理中...';
              clearBtn.setAttribute('disabled', 'true');

              // ✅ 使用后端API解除推荐人
              const response = await fetch('/api/referral/clear-referrer', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${authApi.getToken()}`
                }
              });

              const data = await response.json();

              if (!response.ok) {
                throw new Error(data.error || '解除推荐人失败');
              }
              
              alert('✅ 推荐人关系已解除');
              logger.log('解除推荐人关系');
              
              // 重新渲染
              await this.render();
            } catch (error: any) {
              alert(`❌ ${error.message}`);
              logger.error('解除推荐人失败:', error);
            } finally {
              clearBtn.textContent = '解除';
              clearBtn.removeAttribute('disabled');
            }
          }
        });
      }
    }
  }

  /**
   * 渲染账户信息
   */
  private async renderAccountInfo(account: any): Promise<void> {
    const container = document.getElementById('account-info');
    if (!container) return;

    // ✅ 从后端API获取最新用户信息（包含 referrerId）
    let referrerId: string | null = null;
    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${authApi.getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        referrerId = data.user?.referrerId || null;
        console.log('[User] 获取用户推荐人信息:', { referrerId });
      }
    } catch (error) {
      console.warn('[User] 获取用户信息失败:', error);
    }

    // ✅ 从后端API获取设置（严格模式：必须成功）
    let brokerRebateRate = 0;
    let depositFeeRate = 0;

    try {
      // ✅ 从系统设置读取充值费率（必须成功）
      const systemSettings = await settingsApi.getSystemSettings();
      
      if (!systemSettings || systemSettings.defaultDepositFee === undefined) {
        throw new Error('系统设置读取失败或充值费率未配置');
      }
      
      depositFeeRate = systemSettings.defaultDepositFee;
      
      // ✅ 从交易商设置读取回馈比例
      const brokerSettings = await settingsApi.getBrokerSettings();
      brokerRebateRate = brokerSettings.depositRebateRate || 0;
    } catch (error: any) {
      console.error('[UserPage] 获取设置失败:', error);
      
      // ❌ 严格模式：显示错误信息，不使用默认值
      container.innerHTML = `
        <div style="padding: 40px; text-align: center; background: rgba(248, 81, 73, 0.1); border: 2px solid #f85149; border-radius: 8px;">
          <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
          <h3 style="color: #f85149; margin: 0 0 12px 0;">系统设置读取失败</h3>
          <p style="color: #8b949e; margin: 0 0 16px 0;">
            无法获取充值费率等关键信息，为避免纠纷，请联系管理员。
          </p>
          <p style="color: #f85149; font-size: 12px; font-family: monospace; margin: 0;">
            错误: ${error.message || '未知错误'}
          </p>
        </div>
      `;
      return;
    }

    // ✅ 计算充值费率折扣：条件1-有推荐人 或 条件2-有3+被推荐人
    const hasReferrer = !!referrerId;
    
    // ✅ 获取推荐统计（被推荐人数量）
    let referralCount = 0;
    try {
      const statsResponse = await fetch('/api/referral/stats', {
        headers: {
          'Authorization': `Bearer ${authApi.getToken()}`
        }
      });
      if (statsResponse.ok) {
        const data = await statsResponse.json();
        referralCount = data.stats?.totalReferrals || 0;
        console.log('[User] 获取推荐统计成功:', { totalReferrals: referralCount });
      } else {
        console.warn('[User] 获取推荐统计失败:', statsResponse.status);
      }
    } catch (error) {
      console.warn('[User] 获取推荐统计异常:', error);
    }
    
    const hasThreeReferrals = referralCount >= 3;
    const hasDiscount = hasReferrer || hasThreeReferrals;
    const referralDiscount = hasDiscount ? 0.9 : 1.0; // 享受9折优惠
    const actualDepositFeeRate = depositFeeRate * referralDiscount;
    
    // 折扣原因文本
    let discountReason = '';
    if (hasReferrer) {
      discountReason = '🎁 推荐人优惠';
    } else if (hasThreeReferrals) {
      discountReason = `🎁 推荐达人优惠 (${referralCount}名被推荐人)`;
    }
    
    console.log('[User] 充值费率折扣计算:', {
      referrerId,
      hasReferrer,
      referralCount,
      hasThreeReferrals,
      hasDiscount,
      baseRate: depositFeeRate,
      actualRate: actualDepositFeeRate,
      discountReason
    });
    
    const depositFeeDetails = {
      baseRate: depositFeeRate,
      actualRate: actualDepositFeeRate,
      hasReferralDiscount: hasDiscount,
      discountPercent: hasDiscount ? 10 : 0,  // 折扣百分比
      discountReason: discountReason,
    };

    container.innerHTML = `
      <div class="info-grid">
        <div class="info-item">
          <label>总余额</label>
          <span class="balance-value">${account.balance.toFixed(2)} USDT</span>
        </div>
        <div class="info-item">
          <label>可用余额</label>
          <span class="balance-value">${account.availableBalance.toFixed(2)} USDT</span>
        </div>
        <div class="info-item">
          <label>冻结保证金</label>
          <span class="balance-value">${account.frozenMargin.toFixed(2)} USDT</span>
        </div>
        <div class="info-item">
          <label>已实现盈亏</label>
          <span class="balance-value ${account.realizedPnl >= 0 ? 'positive' : 'negative'}">
            ${account.realizedPnl >= 0 ? '+' : ''}${account.realizedPnl.toFixed(2)} USDT
          </span>
        </div>
        <div class="info-item">
          <label>
            充值费率 (%)
            ${depositFeeDetails.hasReferralDiscount ? 
              `<span style="color: #3fb950; font-size: 11px; margin-left: 4px;">${depositFeeDetails.discountReason}</span>` : 
              `<span style="color: #8b949e; font-size: 11px; margin-left: 4px;">📌 系统默认</span>`
            }
          </label>
          <span class="balance-value">
            ${depositFeeDetails.hasReferralDiscount ? `
              <span style="text-decoration: line-through; color: #8b949e; font-size: 12px; margin-right: 8px;">${formatFeeRate(depositFeeDetails.baseRate)}</span>
              <span style="color: #3fb950; font-weight: bold;">${formatFeeRate(depositFeeDetails.actualRate)}</span>
              <span style="background: linear-gradient(135deg, #3fb950 0%, #2ea043 100%); color: white; padding: 2px 6px; border-radius: 4px; font-size: 11px; margin-left: 6px;">-${depositFeeDetails.discountPercent}%</span>
            ` : formatFeeRate(depositFeeDetails.actualRate)}
          </span>
        </div>
        <div class="info-item">
          <label>
            交易商回馈比例 (%)
            <span style="color: #8b949e; font-size: 11px; margin-left: 4px;">📌 系统默认</span>
          </label>
          <span class="balance-value">${formatFeeRate(brokerRebateRate)}</span>
        </div>
      </div>
      
      <!-- 数据来源说明 -->
      <div style="margin-top: 16px; padding: 12px; background: rgba(139, 148, 158, 0.1); border: 1px solid #30363d; border-radius: 6px; font-size: 12px; color: #8b949e;">
        <strong>📌 说明</strong>：余额相关数据来自后端API，费率相关数据为系统默认配置。交易费率（Maker/Taker）请查看"交易设置"标签。
      </div>
    `;
  }

  /**
   * 获取角色文本
   */
  private getRoleText(role: string): string {
    switch (role) {
      case 'guest': return '游客';
      case 'trader': return '交易者';
      case 'broker': return '交易商';
      case 'admin': return '管理员';
      default: return role;
    }
  }

  /**
   * 渲染交易设置
   */
  private async renderTradingSettings(uid: string): Promise<void> {
    const container = document.getElementById('settings');
    if (!container) return;

    const currentUser = this.userManager.getCurrentUser()!;
    
    // 尝试从后端API加载设置
    let settings: any;
    try {
      const apiSettings = await settingsApi.getTradingSettings();
      
      if (!apiSettings) {
        throw new Error('getTradingSettings 返回 undefined');
      }
      
      // ✅ 尝试从后端获取交易商费率
      let fees = { maker: 0, taker: 0 };  // 只需要交易手续费
      let brokerUid = 'SYSTEM';
      let brokerName = '系统默认';
      
      try {
        const brokerSettings = await settingsApi.getBrokerSettings();
        
        fees = {
          maker: brokerSettings.makerFeeRate,
          taker: brokerSettings.takerFeeRate,
        };
        
        // ✅ 如果有 brokerUid，查找交易商用户名
        if (brokerSettings.brokerUid && brokerSettings.brokerUid !== 'SYSTEM') {
          brokerUid = brokerSettings.brokerUid;
          
          // ✅ 调用后端 API 获取交易商用户名
          try {
            const token = authApi.getToken();
            const response = await fetch(`${API_BASE}/api/auth/user/${brokerUid}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });
            
            if (response.ok) {
              const data = await response.json();
              brokerName = data.user?.username || brokerUid.substring(0, 8);
            } else {
              brokerName = brokerUid.substring(0, 8);  // 使用UUID前8位
            }
          } catch (error) {
            logger.warn('获取交易商用户名失败:', error);
            brokerName = brokerUid.substring(0, 8);
          }
        }
        
        logger.info('✅ 从后端获取交易商费率:', fees);
        logger.info('✅ 交易商信息:', { brokerUid, brokerName });
      } catch (brokerError) {
        logger.warn('用户无交易商或获取失败，使用默认费率:', brokerError);
        // Maker/Taker 保持为 0
        // Deposit 保持为 3%
        // Rebate 保持为 0
        // brokerUid 保持为 'SYSTEM'
        // brokerName 保持为 '系统默认'
      }
      
      settings = {
        uid: uid,
        brokerUid: brokerUid,  // ✅ 使用从后端获取的交易商UID
        brokerName: brokerName,  // ✅ 使用从后端获取的交易商名称
        assignedDate: Date.now(),
        updatedAt: Date.now(),
        limits: {
        },
        riskControl: {
          liquidationThreshold: apiSettings.liquidationThreshold,
        },
        slippage: DEFAULT_TRADING_SETTINGS.slippage,
      };
    } catch (error) {
      logger.warn('无法从后端加载交易设置，使用本地数据:', error);
      settings = this.tradingSettingsManager.getSettings(uid);
      
      if (!settings) {
        // 创建默认设置
        settings = this.tradingSettingsManager.createDefaultSettings(
          uid,
          'SYSTEM',
          '系统默认'
        );
      }
    }

    container.innerHTML = `
      <div class="settings-form-compact">
        <!-- 基础信息 -->
        <div class="settings-basic-info-card">
          <div class="info-badge">
            <strong>交易商名称:</strong> ${settings.brokerName}
            </div>
          <div class="info-badge">
            <strong>分配日期:</strong> ${new Date(settings.assignedDate).toLocaleDateString('zh-CN')}
            </div>
          <div class="info-badge">
            <strong>最后更新:</strong> ${new Date(settings.updatedAt).toLocaleString('zh-CN')}
          </div>
        </div>

        <!-- 设置值表格（只读） -->
        <div class="settings-table-wrapper">
          <table class="settings-table">
            <thead>
              <tr>
                <th style="width: 50%;">设置项</th>
                <th style="width: 50%;">设定值</th>
              </tr>
            </thead>
            <tbody>
        <!-- 交易限制 -->

        <!-- 风控设置 -->
              <tr>
                <td>
                  强平维持率阈值
                <span class="help-icon" title="${TRADING_SETTINGS_HELP.liquidationThreshold}">ⓘ</span>
                </td>
                <td>
                  <div class="input-group">
              <input 
                type="number" 
                      class="table-input" 
                id="liquidation-ratio" 
                value="${settings.riskControl.liquidationThreshold}" 
                min="0" 
                max="100"
                      disabled
              >
                    <span class="input-unit">%</span>
            </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 说明文字 -->
        <div style="margin-top: 16px; padding: 12px; background: rgba(88, 166, 255, 0.1); border: 1px solid rgba(88, 166, 255, 0.3); border-radius: 6px; font-size: 13px; color: #8b949e;">
          ℹ️ 交易设置由系统管理员统一配置，用户无法修改
        </div>
      </div>
    `;
  }

  /**
   * 处理保存交易设置
   */
  private async handleSaveSettings(e: Event, uid: string): Promise<void> {
    e.preventDefault();

    const currentUser = this.userManager.getCurrentUser()!;

    try {
      // 从表单获取设置数据
      const settingsData = {
        liquidationThreshold: parseFloat((document.getElementById('liquidation-ratio') as HTMLInputElement).value),
      };

      // 调用后端API保存设置
      await settingsApi.updateTradingSettings(settingsData);
      
      alert('✅ 交易设置已保存');
      this.render(); // 重新渲染
    } catch (error: any) {
      logger.error('保存交易设置失败:', error);
      alert('❌ 保存失败: ' + (error.message || '未知错误'));
    }
  }

  // ==================== 存取款功能 ====================

  /**
   * 渲染存取款报表
   */
  private renderDepositWithdrawalReport(): void {
    const user = this.userManager.getCurrentUser();
    if (!user) return;

    // 渲染统计卡片
    this.renderDepositStats(user.uid);
    
    // 渲染交易历史
    this.renderTransactionHistory(user.uid);
  }

  /**
   * 渲染统计卡片
   */
  private renderDepositStats(uid: string): void {
    const container = document.getElementById('deposit-stats');
    if (!container) return;

    const stats = this.accountManager.getDepositWithdrawalStats(uid);

    container.innerHTML = `
      <div class="stat-card">
        <div class="stat-label">总存款</div>
        <div class="stat-value positive">${stats.totalDeposit.toFixed(2)} USDT</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">总取款</div>
        <div class="stat-value negative">${stats.totalWithdrawal.toFixed(2)} USDT</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">净流入</div>
        <div class="stat-value ${stats.netFlow >= 0 ? 'positive' : 'negative'}">
          ${stats.netFlow >= 0 ? '+' : ''}${stats.netFlow.toFixed(2)} USDT
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-label">总手续费</div>
        <div class="stat-value negative">${stats.totalDepositFee.toFixed(2)} USDT</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">总回馈收入</div>
        <div class="stat-value positive">${stats.totalBrokerRebate.toFixed(2)} USDT</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">推荐人奖励</div>
        <div class="stat-value positive">${stats.totalReferrerReward.toFixed(2)} USDT</div>
      </div>
    `;
  }

  /**
   * 渲染交易历史
   */
  private async renderTransactionHistory(uid: string): Promise<void> {
    const container = document.getElementById('transaction-history');
    if (!container) return;

    // 获取筛选条件
    const typeFilter = (document.getElementById('filter-type') as HTMLSelectElement)?.value || '';
    const statusFilter = (document.getElementById('filter-status') as HTMLSelectElement)?.value || '';

    try {
      // ✨ 从后端API获取交易历史
      const response = await fetch('/api/accounts/transactions?limit=50', {
        headers: {
          'Authorization': `Bearer ${authApi.getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('获取交易记录失败');
      }

      const data = await response.json();
      let transactions = data.transactions || [];

      // ✅ 去重：检查是否有重复的交易记录 ID
      const seenIds = new Set<string>();
      const duplicateIds: string[] = [];
      const filteredTransactions: any[] = [];
      
      transactions.forEach((tx: any) => {
        if (seenIds.has(tx.id)) {
          duplicateIds.push(tx.id);
        } else {
          seenIds.add(tx.id);
          filteredTransactions.push(tx);
        }
      });
      
      if (duplicateIds.length > 0) {
        console.warn('[UserPage] ⚠️ 发现并过滤了重复的交易记录:', duplicateIds);
      }
      
      transactions = filteredTransactions;

      // 应用筛选条件
      if (typeFilter) {
        transactions = transactions.filter((tx: any) => tx.type === typeFilter);
      }
      if (statusFilter) {
        transactions = transactions.filter((tx: any) => tx.status === statusFilter);
      }

      if (transactions.length === 0) {
        container.innerHTML = `
          <div class="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <line x1="9" y1="9" x2="15" y2="9"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
            <p>暂无交易记录</p>
          </div>
        `;
        return;
      }

      container.innerHTML = `
        <table class="data-table">
          <thead>
            <tr>
              <th>时间</th>
              <th>类型</th>
              <th>金额</th>
              <th>手续费</th>
              <th>余额</th>
              <th>状态</th>
              <th>备注</th>
            </tr>
          </thead>
          <tbody>
            ${transactions.map((tx: any) => this.renderTransactionRow(tx)).join('')}
          </tbody>
        </table>
      `;
    } catch (error) {
      logger.error('获取交易记录失败:', error);
      // 降级到本地数据
    const transactions = this.accountManager.getTransactionHistory(uid, {
      type: typeFilter as any,
      status: statusFilter as any,
      limit: 50
    });

    if (transactions.length === 0) {
      container.innerHTML = `
        <div class="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <line x1="9" y1="9" x2="15" y2="9"/>
            <line x1="9" y1="15" x2="15" y2="15"/>
          </svg>
          <p>暂无交易记录</p>
        </div>
      `;
      return;
    }

    container.innerHTML = `
      <table class="data-table">
        <thead>
          <tr>
            <th>时间</th>
            <th>类型</th>
            <th>金额</th>
            <th>手续费</th>
            <th>余额</th>
            <th>状态</th>
            <th>备注</th>
          </tr>
        </thead>
        <tbody>
          ${transactions.map(tx => this.renderTransactionRow(tx)).join('')}
        </tbody>
      </table>
    `;
    }
  }

  /**
   * 渲染交易记录行
   */
  private renderTransactionRow(tx: any): string {
    const typeIcon = this.getTransactionTypeIcon(tx.type);
    const typeName = this.getTransactionTypeName(tx.type);
    const amountClass = tx.amount >= 0 ? 'positive' : 'negative';
    const statusBadge = this.getStatusBadge(tx.status);
    
    // ✅ 回馈信息显示
    let rebateInfo = '';
    if (tx.type === 'deposit' && tx.metadata?.rebateAmount && tx.metadata.rebateAmount > 0) {
      const rebateAmount = tx.metadata.rebateAmount;
      const rebateProcessed = tx.metadata.rebateProcessed;
      const rebateError = tx.metadata.rebateError;
      
      rebateInfo = `<div style="font-size: 11px; color: #3fb950; margin-top: 4px;">
        🎉 回馈: +${rebateAmount.toFixed(2)} USDT
        ${!rebateProcessed && rebateError ? `<span style="color: #f85149;">(失败)</span>` : ''}
      </div>`;
    }

    return `
      <tr>
        <td>${new Date(tx.createdAt).toLocaleString('zh-CN', {
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })}</td>
        <td>${typeIcon} ${typeName}${rebateInfo}</td>
        <td class="${amountClass}">
          ${tx.amount >= 0 ? '+' : ''}${Number(tx.amount).toFixed(2)} USDT
        </td>
        <td>${tx.fee ? `-${Number(tx.fee).toFixed(2)} USDT` : '-'}</td>
        <td>${tx.balanceAfter ? Number(tx.balanceAfter).toFixed(2) + ' USDT' : '-'}</td>
        <td>${statusBadge}</td>
        <td style="font-size: 12px; color: #8b949e;">${tx.notes || tx.metadata?.notes || '-'}</td>
      </tr>
    `;
  }

  /**
   * 获取交易类型图标
   */
  private getTransactionTypeIcon(type: string): string {
    const icons: Record<string, string> = {
      'deposit': '💰',
      'withdrawal': '💸',
      'deposit_fee': '📉',
      'broker_rebate_in': '🎁',
      'broker_rebate': '🎁',              // ✅ 交易商回馈（收入）
      'broker_rebate_payment': '💸',     // ✅ 交易商回馈支出
      'referrer_reward': '🎁',
      'trading_fee': '📉',
      'realized_pnl': '💹',
      'referral_bonus_deposit': '📤', // 充值到赠金池
      'referral_bonus_received': '🎁', // 获得推荐赠金
      'referral_deposit_reward': '🎁', // ✅ 推荐人充值费奖励
    };
    return icons[type] || '📝';
  }

  /**
   * 获取交易类型名称
   */
  private getTransactionTypeName(type: string): string {
    const names: Record<string, string> = {
      'deposit': '存款',
      'withdrawal': '取款',
      'deposit_fee': '存款手续费',
      'broker_rebate_in': '交易商回馈',
      'broker_rebate': '交易商回馈',        // ✅ 交易商回馈（收入）
      'broker_rebate_payment': '客户存款回馈',  // ✅ 交易商回馈支出
      'referrer_reward': '推荐人奖励',
      'trading_fee': '交易手续费',
      'realized_pnl': '已实现盈亏',
      'referral_bonus_deposit': '充值赠金池', // 充值到赠金池
      'referral_bonus_received': '推荐赠金', // 获得推荐赠金
      'referral_deposit_reward': '推荐充值奖励', // ✅ 推荐人充值费奖励
    };
    return names[type] || type;
  }

  /**
   * 获取状态徽章
   */
  private getStatusBadge(status: string): string {
    const badges: Record<string, string> = {
      'completed': '<span style="color: #3fb950;">✓ 成功</span>',
      'pending': '<span style="color: #f0b90b;">⏳ 待处理</span>',
      'failed': '<span style="color: #f85149;">✗ 失败</span>'
    };
    return badges[status] || status;
  }

  /**
   * 绑定存取款事件
   */
  private attachDepositWithdrawEvents(): void {
    // ✅ 防止重复绑定：如果已经绑定过，直接返回
    if ((this as any)._eventsAttached) {
      return;
    }
    
    // 存款按钮
    const depositBtn = document.getElementById('deposit-btn');
    if (depositBtn) {
      depositBtn.addEventListener('click', () => this.showDepositModal());
    }

    // 取款按钮
    const withdrawBtn = document.getElementById('withdraw-btn');
    if (withdrawBtn) {
      withdrawBtn.addEventListener('click', () => this.showWithdrawModal());
    }

    // 存款金额输入 - 实时计算费用
    const depositAmountInput = document.getElementById('deposit-amount');
    if (depositAmountInput) {
      depositAmountInput.addEventListener('input', (e) => {
        const amount = parseFloat((e.target as HTMLInputElement).value) || 0;
        this.updateDepositPreview(amount);
      });
    }

    // 存款确认
    const depositConfirm = document.getElementById('deposit-confirm');
    if (depositConfirm) {
      depositConfirm.addEventListener('click', () => this.handleDeposit());
    }

    // 取款确认
    const withdrawConfirm = document.getElementById('withdraw-confirm');
    if (withdrawConfirm) {
      withdrawConfirm.addEventListener('click', () => this.handleWithdraw());
    }

    // 关闭按钮
    ['deposit-close', 'deposit-cancel', 'withdraw-close', 'withdraw-cancel'].forEach(id => {
      const btn = document.getElementById(id);
      if (btn) {
        btn.addEventListener('click', () => this.closeAllModals());
      }
    });

    // 刷新按钮
    const refreshBtn = document.getElementById('refresh-transactions');
    if (refreshBtn) {
      refreshBtn.addEventListener('click', () => {
        const user = this.userManager.getCurrentUser();
        if (user) {
          this.renderDepositStats(user.uid);
          this.renderTransactionHistory(user.uid);
        }
      });
    }

    // 筛选器变化
    const filterType = document.getElementById('filter-type');
    const filterStatus = document.getElementById('filter-status');
    if (filterType) {
      filterType.addEventListener('change', () => {
        const user = this.userManager.getCurrentUser();
        if (user) this.renderTransactionHistory(user.uid);
      });
    }
    if (filterStatus) {
      filterStatus.addEventListener('change', () => {
        const user = this.userManager.getCurrentUser();
        if (user) this.renderTransactionHistory(user.uid);
      });
    }
    
    // ✅ 标记为已绑定
    (this as any)._eventsAttached = true;
  }

  /**
   * 显示存款弹窗
   */
  private showDepositModal(): void {
    const modal = document.getElementById('deposit-modal');
    if (modal) {
      modal.style.display = 'flex';
      // 重置表单
      (document.getElementById('deposit-amount') as HTMLInputElement).value = '';
      this.updateDepositPreview(0);
    }
  }

  /**
   * 显示取款弹窗
   */
  private showWithdrawModal(): void {
    const modal = document.getElementById('withdraw-modal');
    if (modal) {
      modal.style.display = 'flex';
      // 重置表单
      (document.getElementById('withdraw-amount') as HTMLInputElement).value = '';
      (document.getElementById('wallet-address') as HTMLInputElement).value = '';
      
      // 显示可用余额
      const user = this.userManager.getCurrentUser();
      if (user) {
        const balanceDisplay = document.getElementById('available-balance-display');
        if (balanceDisplay) {
          // 尝试从后端获取余额
          accountApi.getBalance().then(balance => {
            if (balance) {
            balanceDisplay.textContent = Number(balance.availableBalance).toFixed(2);
            } else {
              balanceDisplay.textContent = '0.00';
            }
          }).catch(error => {
            logger.warn('无法获取余额:', error);
            balanceDisplay.textContent = '0.00';
          });
        }
      }
    }
  }

  /**
   * 关闭所有弹窗
   */
  private closeAllModals(): void {
    const depositModal = document.getElementById('deposit-modal');
    const withdrawModal = document.getElementById('withdraw-modal');
    if (depositModal) depositModal.style.display = 'none';
    if (withdrawModal) withdrawModal.style.display = 'none';
  }

  /**
   * 更新存款预览
   */
  private async updateDepositPreview(amount: number): Promise<void> {
    const user = this.userManager.getCurrentUser();
    if (!user) return;

    try {
      // ✅ 从后端 API 实时获取系统设置的充值费率（严格模式：必须成功）
      const response = await fetch('/api/settings/system', {  // ✅ 使用公开API
        headers: {
          'Authorization': `Bearer ${authApi.getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`API请求失败: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.settings || data.settings.defaultDepositFee === undefined) {
        throw new Error('系统设置中未配置充值费率');
      }
      
      const baseRate = data.settings.defaultDepositFee;
      const hasDiscount = !!user.referrerId;
      const actualRate = hasDiscount ? baseRate * 0.9 : baseRate;  // 推荐人9折
      
      const fee = amount * actualRate;
      const actualAmount = amount - fee;
      
      // 更新UI显示
      const amountEl = document.getElementById('deposit-preview-amount');
      const feeRateEl = document.getElementById('deposit-fee-rate');
      const feeEl = document.getElementById('deposit-preview-fee');
      const actualEl = document.getElementById('deposit-preview-actual');
      
      if (amountEl) amountEl.textContent = `${amount.toFixed(2)} USDT`;
      if (feeRateEl) feeRateEl.textContent = `${(actualRate * 100).toFixed(2)}%`;
      if (feeEl) feeEl.textContent = `-${fee.toFixed(2)} USDT`;
      if (actualEl) actualEl.textContent = `${actualAmount.toFixed(2)} USDT`;
    } catch (error: any) {
      console.error('[UserPage] 获取实时费率失败:', error);
      
      // ❌ 严格模式：显示错误信息，清空预览
      const amountEl = document.getElementById('deposit-preview-amount');
      const feeRateEl = document.getElementById('deposit-fee-rate');
      const feeEl = document.getElementById('deposit-preview-fee');
      const actualEl = document.getElementById('deposit-preview-actual');
      
      if (amountEl) amountEl.textContent = '-';
      if (feeRateEl) feeRateEl.textContent = '无法获取';
      if (feeEl) feeEl.textContent = '-';
      if (actualEl) actualEl.textContent = '-';
      
      alert(`⚠️ 系统错误\n\n无法获取充值费率设置，为避免纠纷，请稍后再试或联系管理员。\n\n错误详情: ${error.message}`);
    }
  }

  /**
   * 处理存款
   */
  private async handleDeposit(): Promise<void> {
    // 2026-07-26：舊 /api/accounts/deposit 憑空上分路已移除
    alert('充值功能已調整：請使用鏈上入金（DCT／USDC）');
    this.closeAllModals();
  }

  /**
   * 处理取款
   */
  private async handleWithdraw(): Promise<void> {
    // 2026-07-26：舊 /api/accounts/withdraw 假出金路已移除
    alert('取款功能已移除：出金請走正規流程（人工出金申請／鏈上出金）');
    this.closeAllModals();
  }

  // ==================== 推荐系统方法 ====================

  /**
   * 渲染推荐系统
   */
  private renderReferralSystem(): void {
    const user = this.userManager.getCurrentUser();
    if (!user) return;

    // 渲染推荐码
    this.renderReferralCodes(user);

    // 渲染推荐统计
    this.renderReferralStats(user.uid);

    // 渲染被推荐用户列表
    this.renderReferredUsers(user.uid);
  }

  /**
   * 渲染推荐码
   */
  private async renderReferralCodes(user: any): Promise<void> {
    const container = document.getElementById('referral-codes-list');
    if (!container) return;

    try {
      // 显示加载状态
      container.innerHTML = `
        <div class="referral-codes-loading">
          <div class="loading-spinner"></div>
          <p>加载中...</p>
        </div>
      `;

      // 获取推荐码列表
      const codes = await referralApi.getUserReferralCodes();
      this.currentReferralCodes = codes; // 保存到实例变量

      if (codes.length === 0) {
        container.innerHTML = `
          <div class="referral-codes-empty">
            <p>😊 您还没有推荐码</p>
            <p>点击上方「创建新推荐码」按钮开始吧！</p>
          </div>
        `;
        return;
      }

      // 渲染推荐码卡片
      container.innerHTML = codes.map(code => this.renderReferralCodeCard(code)).join('');
    } catch (error: any) {
      logger.error('渲染推荐码列表失败:', error);
      container.innerHTML = `
        <div class="referral-codes-empty">
          <p>❌ 加载失败: ${error.message}</p>
        </div>
      `;
    }
  }

  /**
   * 渲染单个推荐码卡片
   */
  private renderReferralCodeCard(code: any): string {
    const typeBadgeClass = code.type === 'default' ? 'default' : code.type === 'special' ? 'special' : 'custom';
    const statusBadgeClass = code.isActive ? 'active' : 'inactive';
    const typeLabel = this.getTypeLabel(code.type);
    const statusLabel = code.isActive ? '启用' : '禁用';

    return `
      <div class="referral-code-card ${code.isActive ? 'active' : 'inactive'}" data-code-id="${code.id}">
        <div class="code-header">
          <span class="code">${code.code}</span>
          <span class="type-badge ${typeBadgeClass}">${typeLabel}</span>
          <span class="status-badge ${statusBadgeClass}">${statusLabel}</span>
        </div>
        
        <div class="code-stats">
          <div class="stat">
            <label>赠金池余额</label>
            <div class="value highlight">${code.bonusPoolBalance.toFixed(2)} USDT</div>
          </div>
          <div class="stat">
            <label>每次赠金</label>
            <div class="value">${code.bonusPerUse.toFixed(2)} USDT</div>
          </div>
          <div class="stat">
            <label>使用次数</label>
            <div class="value">${code.usageCount}</div>
          </div>
          <div class="stat">
            <label>累计赠出</label>
            <div class="value">${code.totalBonusGiven.toFixed(2)} USDT</div>
          </div>
        </div>
        
        ${code.description ? `<div class="code-description">📝 ${code.description}</div>` : ''}
        
        <div class="code-actions">
          <button class="primary" onclick="window.userPage.openDepositDialog('${code.id}')">
            💰 充值
          </button>
          <button onclick="window.userPage.openBonusSettingsDialog('${code.id}')">
            ⚙️ 设置
          </button>
          <button onclick="window.userPage.viewCodeDetails('${code.id}')">
            📋 详情
          </button>
        </div>
      </div>
    `;
  }

  private getTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      default: '默认',
      custom: '自定义',
      special: '特殊',
    };
    return labels[type] || type;
  }

  /**
   * 打开创建推荐码对话框
   */
  public openCreateCodeDialog(): void {
    logger.info('Opening create referral code dialog...');
    const modal = document.getElementById('create-referral-code-modal');
    if (!modal) {
      logger.error('Create referral code modal not found');
      return;
    }

    // 清空表单
    const codeInput = document.getElementById('new-code-input') as HTMLInputElement;
    const descInput = document.getElementById('new-code-description') as HTMLInputElement;
    const expiresInput = document.getElementById('new-code-expires') as HTMLInputElement;
    
    if (codeInput) codeInput.value = '';
    if (descInput) descInput.value = '';
    if (expiresInput) expiresInput.value = '';

    modal.style.display = 'flex';
    logger.info('Modal opened successfully');
  }

  /**
   * 处理创建推荐码
   */
  private _isCreating = false; // 🆕 防抖标记
  public async handleCreateCode(): Promise<void> {
    // 🆕 防止重复提交
    if (this._isCreating) {
      logger.warn('创建推荐码请求进行中，忽略重复点击');
      return;
    }

    const codeInput = document.getElementById('new-code-input') as HTMLInputElement;
    const descInput = document.getElementById('new-code-description') as HTMLInputElement;
    const expiresInput = document.getElementById('new-code-expires') as HTMLInputElement;

    const code = codeInput.value.trim().toUpperCase();
    const description = descInput.value.trim();
    const expires = expiresInput.value ? new Date(expiresInput.value) : undefined;

    if (!code) {
      alert('请输入推荐码');
      return;
    }

    if (!/^[A-Z0-9]{6,12}$/.test(code)) {
      alert('推荐码必须是6-12位大写字母或数字');
      return;
    }

    this._isCreating = true; // 🆕 设置标记
    try {
      const result = await referralApi.createReferralCode(code, description, expires);
      alert(result.message || '创建成功！');
      
      // 关闭模态框
      const modal = document.getElementById('create-referral-code-modal');
      if (modal) modal.style.display = 'none';
      
      // 刷新列表
      const user = this.userManager.getCurrentUser();
      if (user) await this.renderReferralCodes(user);
    } catch (error: any) {
      logger.error('创建推荐码异常:', error);
      alert(`创建失败：${error.message}`);
    } finally {
      this._isCreating = false; // 🆕 重置标记
    }
  }

  /**
   * 打开充值对话框
   */
  public async openDepositDialog(codeId: string): Promise<void> {
    logger.info('Opening deposit dialog for code:', codeId);
    
    // 查找推荐码
    const code = this.currentReferralCodes.find(c => c.id === codeId);
    if (!code) {
      alert('推荐码不存在');
      return;
    }

    this.currentCodeForOperation = code;

    // 获取账户余额
    try {
      const account = await accountApi.getAccount();

      // 填充数据
      const codeEl = document.getElementById('deposit-pool-code');
      const currentBalanceEl = document.getElementById('deposit-pool-current-balance');
      const accountBalanceEl = document.getElementById('deposit-pool-account-balance');
      const amountInput = document.getElementById('deposit-pool-amount') as HTMLInputElement;
      const summary = document.getElementById('deposit-pool-summary');

      if (codeEl) codeEl.textContent = code.code;
      if (currentBalanceEl) currentBalanceEl.textContent = `${code.bonusPoolBalance.toFixed(2)} USDT`;
      if (accountBalanceEl) accountBalanceEl.textContent = `${account!.availableBalance.toFixed(2)} USDT`;

      if (amountInput) {
        amountInput.value = '';
        amountInput.dataset.codeId = codeId;
        amountInput.dataset.currentBalance = code.bonusPoolBalance.toString();
        amountInput.dataset.bonusPerUse = code.bonusPerUse.toString();
      }

      if (summary) summary.style.display = 'none';

      // 显示模态框
      const modal = document.getElementById('deposit-bonus-pool-modal');
      if (modal) modal.style.display = 'flex';
    } catch (error: any) {
      alert(`获取账户信息失败：${error.message}`);
    }
  }

  /**
   * 打开设置赠金对话框
   */
  public async openBonusSettingsDialog(codeId: string): Promise<void> {
    logger.info('Opening bonus settings dialog for code:', codeId);
    
    // 查找推荐码
    const code = this.currentReferralCodes.find(c => c.id === codeId);
    if (!code) {
      alert('推荐码不存在');
      return;
    }

    this.currentCodeForOperation = code;

    // 填充数据
    const codeEl = document.getElementById('set-bonus-code');
    const balanceEl = document.getElementById('set-bonus-balance');
    const currentEl = document.getElementById('set-bonus-current');
    const amountInput = document.getElementById('set-bonus-amount') as HTMLInputElement;
    const preview = document.getElementById('set-bonus-preview');

    if (codeEl) codeEl.textContent = code.code;
    if (balanceEl) balanceEl.textContent = `${code.bonusPoolBalance.toFixed(2)} USDT`;
    if (currentEl) currentEl.textContent = `${code.bonusPerUse.toFixed(2)} USDT`;
    
    if (amountInput) {
      amountInput.value = code.bonusPerUse.toString();
      amountInput.dataset.codeId = codeId;
      amountInput.dataset.balance = code.bonusPoolBalance.toString();
    }

    if (preview) preview.style.display = 'none';

    // 显示模态框
    const modal = document.getElementById('set-bonus-per-use-modal');
    if (modal) modal.style.display = 'flex';
  }

  /**
   * 查看推荐码详情
   */
  public async viewCodeDetails(codeId: string): Promise<void> {
    logger.info('Viewing code details for:', codeId);
    
    // 查找推荐码
    const code = this.currentReferralCodes.find(c => c.id === codeId);
    if (!code) {
      alert('推荐码不存在');
      return;
    }

    this.currentCodeForOperation = code;

    try {
      // 获取交易记录
      const transactions = await referralApi.getBonusTransactions(codeId);

      // 填充基本信息
      this.fillCodeDetailModal(code, transactions);

      // 显示模态框
      const modal = document.getElementById('referral-code-detail-modal');
      if (modal) modal.style.display = 'flex';
    } catch (error: any) {
      alert(`加载详情失败：${error.message}`);
    }
  }

  /**
   * 填充推荐码详情模态框
   */
  private fillCodeDetailModal(code: any, transactions: any[]): void {
    // 基本信息
    const detailCode = document.getElementById('detail-code');
    const detailType = document.getElementById('detail-type');
    const detailStatus = document.getElementById('detail-status');
    const detailUsageCount = document.getElementById('detail-usage-count');
    const detailDescription = document.getElementById('detail-description') as HTMLInputElement;

    if (detailCode) detailCode.textContent = code.code;
    if (detailType) {
      detailType.textContent = this.getTypeLabel(code.type);
      detailType.className = `type-badge ${code.type}`;
    }
    if (detailStatus) {
      detailStatus.textContent = code.isActive ? '启用' : '禁用';
      detailStatus.className = `status-badge ${code.isActive ? 'active' : 'inactive'}`;
    }
    if (detailUsageCount) detailUsageCount.textContent = code.usageCount.toString();
    if (detailDescription) detailDescription.value = code.description || '';

    // 赠金池状态
    const detailBalance = document.getElementById('detail-balance');
    const detailUserDeposit = document.getElementById('detail-user-deposit');
    const detailPlatformBonus = document.getElementById('detail-platform-bonus');
    const detailTotalGiven = document.getElementById('detail-total-given');
    const detailBonusPerUse = document.getElementById('detail-bonus-per-use');
    const detailRemainingUses = document.getElementById('detail-remaining-uses');

    if (detailBalance) detailBalance.textContent = `${code.bonusPoolBalance.toFixed(2)} USDT`;
    if (detailUserDeposit) detailUserDeposit.textContent = `${code.userDepositAmount.toFixed(2)} USDT`;
    if (detailPlatformBonus) detailPlatformBonus.textContent = `${code.platformBonusAmount.toFixed(2)} USDT`;
    if (detailTotalGiven) detailTotalGiven.textContent = `${code.totalBonusGiven.toFixed(2)} USDT`;
    if (detailBonusPerUse) detailBonusPerUse.textContent = `${code.bonusPerUse.toFixed(2)} USDT`;
    
    const remainingUses = code.bonusPerUse > 0 ? Math.floor(code.bonusPoolBalance / code.bonusPerUse) : 0;
    if (detailRemainingUses) detailRemainingUses.textContent = remainingUses.toString();

    // 交易记录
    const tbody = document.querySelector('#detail-transactions tbody');
    if (tbody) {
      if (transactions.length === 0) {
        tbody.innerHTML = `
          <tr>
            <td colspan="5" style="text-align: center; color: #8b949e;">暂无交易记录</td>
          </tr>
        `;
      } else {
        tbody.innerHTML = transactions.map(tx => `
          <tr>
            <td>${this.formatDate(tx.createdAt)}</td>
            <td class="type-cell">${this.getTransactionTypeLabel(tx.transactionType)}</td>
            <td class="${tx.amount >= 0 ? 'positive' : 'negative'}">
              ${tx.amount >= 0 ? '+' : ''}${tx.amount.toFixed(2)} USDT
            </td>
            <td>${tx.balanceBefore.toFixed(2)} → ${tx.balanceAfter.toFixed(2)}</td>
            <td>${tx.description || '-'}</td>
          </tr>
        `).join('');
      }
    }
  }

  /**
   * 获取类型标签
   */
  private getTransactionTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      user_deposit: '💰 用户充值',
      platform_grant: '🎁 平台赠金',
      bonus_given: '📤 赠金发放',
    };
    return labels[type] || type;
  }

  /**
   * 格式化日期
   */
  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * 渲染推荐统计
   */
  private async renderReferralStats(uid: string): Promise<void> {
    const container = document.getElementById('referral-stats');
    if (!container) return;

    try {
      // 从后端API获取推荐统计
      const stats = await referralApi.getStats();

      // 获取推荐奖励费率
      let referralRewardRate = 0;
      try {
        const brokerSettings = await settingsApi.getBrokerSettings();
        if (brokerSettings && typeof brokerSettings.referralDepositRewardRate === 'number') {
          referralRewardRate = brokerSettings.referralDepositRewardRate;
        }
      } catch (error) {
        logger.warn('获取推荐奖励费率失败:', error);
      }

      container.innerHTML = `
        <div class="stat-card">
          <div class="stat-label">推荐人数</div>
          <div class="stat-value">${stats.totalReferrals}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">活跃推荐人数</div>
          <div class="stat-value">${stats.activeReferrals}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">总奖励金额</div>
          <div class="stat-value positive">${stats.totalRewards.toFixed(2)} USDT</div>
          <div class="stat-sublabel">奖励费率 ${(referralRewardRate * 100).toFixed(2)}%</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">近30天奖励</div>
          <div class="stat-value positive">${stats.recentRewards.toFixed(2)} USDT</div>
        </div>
      `;
    } catch (error) {
      logger.warn('无法从后端加载推荐统计，使用本地数据:', error);
      
      // 降级到本地数据
      const referredCount = this.userManager.getReferredUserCount(uid);
      const rewardStats = this.accountManager.getReferrerRewardStats(uid);

      container.innerHTML = `
        <div class="stat-card">
          <div class="stat-label">推荐人数</div>
          <div class="stat-value">${referredCount}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">总奖励金额</div>
          <div class="stat-value positive">${rewardStats.totalRewards.toFixed(2)} USDT</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">奖励次数</div>
          <div class="stat-value">${rewardStats.rewardCount}</div>
        </div>
      `;
    }
  }

  /**
   * 渲染被推荐用户列表
   */
  private async renderReferredUsers(uid: string): Promise<void> {
    const container = document.getElementById('referred-users-list');
    if (!container) return;

    try {
      // 从后端API获取推荐列表
      const referrals = await referralApi.getReferralList();

      if (referrals.length === 0) {
        container.innerHTML = `
          <div class="referred-users-empty">
            暂无被推荐用户
          </div>
        `;
        return;
      }

      // 构建表格
      let tableHTML = `
        <div class="referred-users-table">
          <table>
            <thead>
              <tr>
                <th>用户名</th>
                <th>角色</th>
                <th>状态</th>
                <th>注册时间</th>
                <th>累计奖励</th>
              </tr>
            </thead>
            <tbody>
      `;

      referrals.forEach(referral => {
        const roleText = this.getRoleText(referral.role);
        const statusText = referral.status === 'active' ? '活跃' : '非活跃';
        const statusClass = referral.status === 'active' ? 'positive' : 'negative';

        tableHTML += `
          <tr>
            <td>${referral.username}</td>
            <td>${roleText}</td>
            <td class="${statusClass}">${statusText}</td>
            <td>${new Date(referral.createdAt).toLocaleString('zh-CN')}</td>
            <td class="positive">+${referral.totalRewards.toFixed(2)} USDT</td>
          </tr>
        `;
      });

      tableHTML += `
            </tbody>
          </table>
        </div>
      `;

      container.innerHTML = tableHTML;
    } catch (error) {
      logger.warn('无法从后端加载推荐列表，使用本地数据:', error);
      
      // 降级到本地数据
      const referredUsers = this.userManager.getReferredUsers(uid);

      if (referredUsers.length === 0) {
        container.innerHTML = `
          <div class="referred-users-empty">
            暂无被推荐用户
          </div>
        `;
        return;
      }

      let tableHTML = `
        <div class="referred-users-table">
          <table>
            <thead>
              <tr>
                <th>UID</th>
                <th>使用的推荐码</th>
                <th>注册时间</th>
              </tr>
            </thead>
            <tbody>
      `;

      referredUsers.forEach(user => {
        const usedReferralCode = user.profile?.referrerCode || 'N/A';

        tableHTML += `
          <tr>
            <td>${user.uid}</td>
            <td>${usedReferralCode}</td>
            <td>${new Date(user.createdAt).toLocaleString('zh-CN')}</td>
          </tr>
        `;
      });

      tableHTML += `
            </tbody>
          </table>
        </div>
      `;

      container.innerHTML = tableHTML;
    }
  }

  /**
   * 绑定推荐系统事件
   */
  private attachReferralEvents(): void {
    // 防止重复绑定
    if (this.referralEventsAttached) {
      logger.info('Referral events already attached, skipping...');
      return;
    }

    logger.info('Attaching referral events...');

    // 创建新推荐码按钮（新增）
    const createCodeBtn = document.getElementById('create-referral-code-btn');
    if (createCodeBtn) {
      createCodeBtn.addEventListener('click', () => {
        logger.info('Create referral code button clicked');
        this.openCreateCodeDialog();
      });
      logger.info('Create button event attached');
    }

    // 绑定创建推荐码模态框事件（新增）
    this.attachCreateCodeModalEvents();
    
    // 绑定充值模态框事件
    this.attachDepositPoolModalEvents();
    
    // 绑定设置赠金模态框事件
    this.attachBonusSettingsModalEvents();
    
    // 绑定详情模态框事件
    this.attachCodeDetailModalEvents();

    // 复制默认推荐码
    const copyDefaultBtn = document.getElementById('copy-default-code');
    if (copyDefaultBtn) {
      copyDefaultBtn.addEventListener('click', () => {
        const code = document.getElementById('default-referral-code')?.textContent;
        if (code) {
          this.copyToClipboard(code);
          alert('推荐码已复制到剪贴板');
        }
      });
    }

    // 复制自定义推荐码
    const copyCustomBtn = document.getElementById('copy-custom-code');
    if (copyCustomBtn) {
      copyCustomBtn.addEventListener('click', () => {
        const code = document.getElementById('custom-referral-code')?.textContent;
        if (code) {
          this.copyToClipboard(code);
          alert('推荐码已复制到剪贴板');
        }
      });
    }

    // 显示自定义推荐码表单
    const showFormBtn = document.getElementById('show-custom-code-form');
    const formArea = document.getElementById('custom-code-input-area');
    if (showFormBtn && formArea) {
      showFormBtn.addEventListener('click', () => {
        formArea.style.display = 'block';
        showFormBtn.style.display = 'none';
      });
    }

    // 取消自定义推荐码
    const cancelBtn = document.getElementById('cancel-custom-code');
    if (cancelBtn && formArea && showFormBtn) {
      cancelBtn.addEventListener('click', () => {
        formArea.style.display = 'none';
        showFormBtn.style.display = 'block';
        (document.getElementById('custom-code-input') as HTMLInputElement).value = '';
      });
    }

    // 保存自定义推荐码
    const saveBtn = document.getElementById('save-custom-code');
    if (saveBtn) {
      saveBtn.addEventListener('click', () => {
        this.handleSetCustomCode();
      });
    }

    this.referralEventsAttached = true;
    logger.info('All referral events attached successfully');
  }

  /**
   * 绑定创建推荐码模态框事件
   */
  private attachCreateCodeModalEvents(): void {
    const modal = document.getElementById('create-referral-code-modal');
    if (!modal) return;

    // 检查是否已经绑定过（防止重复绑定）
    if ((modal as any)._eventsAttached) {
      logger.info('Modal events already attached, skipping...');
      return;
    }

    // 关闭按钮
    const closeBtn = document.getElementById('create-code-close');
    const cancelBtn = document.getElementById('create-code-cancel');
    const confirmBtn = document.getElementById('create-code-confirm');

    // 🔧 移除旧的事件监听器（如果存在）
    if (closeBtn) {
      const newCloseBtn = closeBtn.cloneNode(true) as HTMLElement;
      closeBtn.parentNode?.replaceChild(newCloseBtn, closeBtn);
      newCloseBtn.addEventListener('click', () => {
        logger.info('Close button clicked');
        modal.style.display = 'none';
      });
    }
    
    if (cancelBtn) {
      const newCancelBtn = cancelBtn.cloneNode(true) as HTMLElement;
      cancelBtn.parentNode?.replaceChild(newCancelBtn, cancelBtn);
      newCancelBtn.addEventListener('click', () => {
        logger.info('Cancel button clicked');
        modal.style.display = 'none';
      });
    }
    
    if (confirmBtn) {
      const newConfirmBtn = confirmBtn.cloneNode(true) as HTMLElement;
      confirmBtn.parentNode?.replaceChild(newConfirmBtn, confirmBtn);
      newConfirmBtn.addEventListener('click', () => {
        logger.info('Confirm button clicked');
        this.handleCreateCode();
      });
    }

    // 点击遮罩层关闭（使用事件捕获防止冒泡问题）
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        logger.info('Modal overlay clicked, closing...');
        modal.style.display = 'none';
      }
    }, false); // 使用冒泡阶段，但只在直接点击modal时触发

    // 自动转换为大写
    const codeInput = document.getElementById('new-code-input') as HTMLInputElement;
    if (codeInput) {
      codeInput.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement;
        target.value = target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      });
    }

    // 标记已绑定
    (modal as any)._eventsAttached = true;
    logger.info('Modal events attached successfully');
  }

  /**
   * 处理设置自定义推荐码
   */
  private async handleSetCustomCode(): Promise<void> {
    const input = document.getElementById('custom-code-input') as HTMLInputElement;
    const customCode = input.value.trim().toUpperCase(); // 转换为大写

    if (!customCode) {
      alert('请输入自定义推荐码');
      return;
    }

    // 验证格式
    if (!/^[A-Z0-9]{6,12}$/.test(customCode)) {
      alert('自定义推荐码必须是6-12位大写字母或数字');
      return;
    }

    try {
      // 调用后端API设置自定义推荐码
      const result = await referralApi.setCustomReferralCode(customCode);

    if (result.success) {
      alert('自定义推荐码设置成功！');
        
        // 更新 localStorage 中的用户数据
        if (result.user) {
          const users = JSON.parse(localStorage.getItem('users') || '{}');
          const session = JSON.parse(localStorage.getItem('current_session') || '{}');
          
          if (session.uid && users[session.uid]) {
            users[session.uid].customReferralCode = result.user.customReferralCode;
            localStorage.setItem('users', JSON.stringify(users));
          }
        }
        
        // 刷新页面
        this.render();
    } else {
        alert(`设置失败：${result.message}`);
      }
    } catch (error: any) {
      alert(`设置失败：${error.message || '未知错误'}`);
      logger.error('设置自定义推荐码失败:', error);
    }
  }

  /**
   * 复制到剪贴板
   */
  private copyToClipboard(text: string): void {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    } else {
      // 兼容旧浏览器
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }

  /**
   * 绑定充值模态框事件
   */
  private attachDepositPoolModalEvents(): void {
    const modal = document.getElementById('deposit-bonus-pool-modal');
    if (!modal || (modal as any)._eventsAttached) return;

    const closeBtn = document.getElementById('deposit-pool-close');
    const cancelBtn = document.getElementById('deposit-pool-cancel');
    const confirmBtn = document.getElementById('deposit-pool-confirm');
    const amountInput = document.getElementById('deposit-pool-amount') as HTMLInputElement;

    if (closeBtn) closeBtn.addEventListener('click', () => modal.style.display = 'none');
    if (cancelBtn) cancelBtn.addEventListener('click', () => modal.style.display = 'none');
    if (confirmBtn) confirmBtn.addEventListener('click', () => this.handleDepositToPool());

    // 输入金额时更新摘要
    if (amountInput) {
      amountInput.addEventListener('input', () => this.updateDepositSummary());
    }

    // 点击遮罩层关闭
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.style.display = 'none';
    });

    (modal as any)._eventsAttached = true;
    logger.info('Deposit pool modal events attached');
  }

  /**
   * 更新充值摘要
   */
  private updateDepositSummary(): void {
    const amountInput = document.getElementById('deposit-pool-amount') as HTMLInputElement;
    const summary = document.getElementById('deposit-pool-summary');
    
    if (!amountInput || !summary) return;

    const amount = parseFloat(amountInput.value) || 0;
    const currentBalance = parseFloat(amountInput.dataset.currentBalance || '0');
    const bonusPerUse = parseFloat(amountInput.dataset.bonusPerUse || '0');

    if (amount > 0) {
      const newBalance = currentBalance + amount;
      const estimatedUses = bonusPerUse > 0 ? Math.floor(newBalance / bonusPerUse) : 0;

      const newBalanceEl = document.getElementById('deposit-pool-new-balance');
      const estimatedUsesEl = document.getElementById('deposit-pool-estimated-uses');

      if (newBalanceEl) newBalanceEl.textContent = `${newBalance.toFixed(2)} USDT`;
      if (estimatedUsesEl) estimatedUsesEl.textContent = estimatedUses.toString();
      
      summary.style.display = 'block';
    } else {
      summary.style.display = 'none';
    }
  }

  /**
   * 处理充值到赠金池
   */
  private async handleDepositToPool(): Promise<void> {
    const amountInput = document.getElementById('deposit-pool-amount') as HTMLInputElement;
    const amount = parseFloat(amountInput.value);
    const codeId = amountInput.dataset.codeId;

    if (!codeId || !amount || amount <= 0) {
      alert('请输入有效的充值金额');
      return;
    }

    try {
      await referralApi.depositToBonusPool(codeId, amount);
      alert('充值成功！');
      
      // 关闭模态框
      const modal = document.getElementById('deposit-bonus-pool-modal');
      if (modal) modal.style.display = 'none';
      
      // 刷新列表
      const user = this.userManager.getCurrentUser();
      if (user) await this.renderReferralCodes(user);
    } catch (error: any) {
      alert(`充值失败：${error.message}`);
    }
  }

  /**
   * 绑定设置赠金模态框事件
   */
  private attachBonusSettingsModalEvents(): void {
    const modal = document.getElementById('set-bonus-per-use-modal');
    if (!modal || (modal as any)._eventsAttached) return;

    const closeBtn = document.getElementById('set-bonus-close');
    const cancelBtn = document.getElementById('set-bonus-cancel');
    const confirmBtn = document.getElementById('set-bonus-confirm');
    const amountInput = document.getElementById('set-bonus-amount') as HTMLInputElement;

    if (closeBtn) closeBtn.addEventListener('click', () => modal.style.display = 'none');
    if (cancelBtn) cancelBtn.addEventListener('click', () => modal.style.display = 'none');
    if (confirmBtn) confirmBtn.addEventListener('click', () => this.handleSetBonusPerUse());

    // 输入金额时更新预览
    if (amountInput) {
      amountInput.addEventListener('input', () => this.updateBonusPreview());
    }

    // 点击遮罩层关闭
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.style.display = 'none';
    });

    (modal as any)._eventsAttached = true;
    logger.info('Bonus settings modal events attached');
  }

  /**
   * 更新赠金预览
   */
  private updateBonusPreview(): void {
    const amountInput = document.getElementById('set-bonus-amount') as HTMLInputElement;
    const preview = document.getElementById('set-bonus-preview');
    
    if (!amountInput || !preview) return;

    const bonusPerUse = parseFloat(amountInput.value) || 0;
    const balance = parseFloat(amountInput.dataset.balance || '0');

    if (bonusPerUse > 0) {
      const estimatedUses = Math.floor(balance / bonusPerUse);
      const estimatedUsesEl = document.getElementById('set-bonus-estimated-uses');
      
      if (estimatedUsesEl) estimatedUsesEl.textContent = estimatedUses.toString();
      preview.style.display = 'block';
    } else {
      preview.style.display = 'none';
    }
  }

  /**
   * 处理设置赠金额度
   */
  private async handleSetBonusPerUse(): Promise<void> {
    const amountInput = document.getElementById('set-bonus-amount') as HTMLInputElement;
    const bonusPerUse = parseFloat(amountInput.value);
    const codeId = amountInput.dataset.codeId;

    if (!codeId || bonusPerUse < 0) {
      alert('请输入有效的赠金额度');
      return;
    }

    try {
      await referralApi.updateReferralCode(codeId, { bonusPerUse });
      alert('设置成功！');
      
      // 关闭模态框
      const modal = document.getElementById('set-bonus-per-use-modal');
      if (modal) modal.style.display = 'none';
      
      // 刷新列表
      const user = this.userManager.getCurrentUser();
      if (user) await this.renderReferralCodes(user);
    } catch (error: any) {
      alert(`设置失败：${error.message}`);
    }
  }

  /**
   * 绑定详情模态框事件
   */
  private attachCodeDetailModalEvents(): void {
    const modal = document.getElementById('referral-code-detail-modal');
    if (!modal || (modal as any)._eventsAttached) return;

    const closeBtn = document.getElementById('code-detail-close');
    const closeBtnFooter = document.getElementById('detail-close-btn');
    const copyBtn = document.getElementById('detail-copy-code');
    const saveDescBtn = document.getElementById('save-description');
    const depositBtn = document.getElementById('detail-deposit-btn');
    const setBonusBtn = document.getElementById('detail-set-bonus-btn');
    const toggleBtn = document.getElementById('detail-toggle-status');
    const deleteBtn = document.getElementById('detail-delete-code');

    if (closeBtn) closeBtn.addEventListener('click', () => modal.style.display = 'none');
    if (closeBtnFooter) closeBtnFooter.addEventListener('click', () => modal.style.display = 'none');
    
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        if (this.currentCodeForOperation) {
          this.copyToClipboard(this.currentCodeForOperation.code);
          alert('推荐码已复制到剪贴板');
        }
      });
    }

    if (saveDescBtn) {
      saveDescBtn.addEventListener('click', () => this.handleSaveDescription());
    }

    if (depositBtn) {
      depositBtn.addEventListener('click', () => {
        if (this.currentCodeForOperation) {
          modal.style.display = 'none';
          this.openDepositDialog(this.currentCodeForOperation.id);
        }
      });
    }

    if (setBonusBtn) {
      setBonusBtn.addEventListener('click', () => {
        if (this.currentCodeForOperation) {
          modal.style.display = 'none';
          this.openBonusSettingsDialog(this.currentCodeForOperation.id);
        }
      });
    }

    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => this.handleToggleCodeStatus());
    }

    if (deleteBtn) {
      deleteBtn.addEventListener('click', () => this.handleDeleteCode());
    }

    // 点击遮罩层关闭
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.style.display = 'none';
    });

    (modal as any)._eventsAttached = true;
    logger.info('Code detail modal events attached');
  }

  /**
   * 保存推荐码描述
   */
  private async handleSaveDescription(): Promise<void> {
    if (!this.currentCodeForOperation) return;

    const descInput = document.getElementById('detail-description') as HTMLInputElement;
    const newDescription = descInput.value.trim();

    try {
      await referralApi.updateReferralCode(this.currentCodeForOperation.id, {
        description: newDescription
      });
      alert('描述已更新');
      
      // 刷新列表
      const user = this.userManager.getCurrentUser();
      if (user) await this.renderReferralCodes(user);
    } catch (error: any) {
      alert(`更新失败：${error.message}`);
    }
  }

  /**
   * 切换推荐码状态
   */
  private async handleToggleCodeStatus(): Promise<void> {
    if (!this.currentCodeForOperation) return;

    const action = this.currentCodeForOperation.isActive ? '禁用' : '启用';
    if (!confirm(`确定要${action}该推荐码吗？`)) return;

    try {
      await referralApi.updateReferralCode(this.currentCodeForOperation.id, {
        isActive: !this.currentCodeForOperation.isActive
      });
      alert(`${action}成功`);
      
      // 关闭模态框
      const modal = document.getElementById('referral-code-detail-modal');
      if (modal) modal.style.display = 'none';
      
      // 刷新列表
      const user = this.userManager.getCurrentUser();
      if (user) await this.renderReferralCodes(user);
    } catch (error: any) {
      alert(`操作失败：${error.message}`);
    }
  }

  /**
   * 删除推荐码
   */
  private async handleDeleteCode(): Promise<void> {
    if (!this.currentCodeForOperation) return;

    if (!confirm(`确定要删除推荐码「${this.currentCodeForOperation.code}」吗？\n\n注意：已被使用的推荐码无法删除。`)) return;

    try {
      await referralApi.deleteReferralCode(this.currentCodeForOperation.id);
      alert('推荐码已删除');
      
      // 关闭模态框
      const modal = document.getElementById('referral-code-detail-modal');
      if (modal) modal.style.display = 'none';
      
      // 刷新列表
      const user = this.userManager.getCurrentUser();
      if (user) await this.renderReferralCodes(user);
    } catch (error: any) {
      alert(`删除失败：${error.message}`);
    }
  }
}

// 初始化用户页面
// 只在 user.html 頁面才自動初始化，避免被 chart.ts import 時觸發 redirect
const _isUserPage = typeof window !== 'undefined' && window.location.pathname.endsWith('user.html');
const userPage = _isUserPage ? new UserPage() : null;

// 导出到全局作用域（供 HTML onclick 使用）
if (userPage) (window as any).userPage = userPage;

// ===== 导出给 trader.ts 使用的渲染函数 =====

/**
 * 渲染个人信息到指定容器
 * @param containerId 容器ID
 */
export async function renderUserInfoToContainer(containerId: string): Promise<void> {
  const container = document.getElementById(containerId);
  if (!container) {
    logger.error(`容器 ${containerId} 不存在`);
    return;
  }

  try {
    // 获取当前用户信息
    const response = await fetch('/api/auth/me', {
      headers: {
        'Authorization': `Bearer ${authApi.getToken()}`
      }
    });

    if (!response.ok) {
      throw new Error(`API返回错误: ${response.status}`);
    }

    const data = await response.json();
    if (!data.user) {
      throw new Error('API未返回用户数据');
    }

    const user = data.user;
    const hoursAfterRegistration = (Date.now() - new Date(user.createdAt).getTime()) / (1000 * 60 * 60);
    const canEditReferrer = hoursAfterRegistration <= 48;

    // 渲染HTML — 頭像橫排 + CSS 變數 + 綁定狀態
    const initials = (user.displayName || user.username || '?').slice(0, 2).toUpperCase();
    const roleLabel: Record<string, string> = { trader: '交易者', broker: '交易商', admin: '管理員' };
    const brokerName = (data as any).brokerInfo?.displayName || '';
    container.innerHTML = `
      <div style="padding:16px;max-height:calc(100vh - 240px);overflow-y:auto;">
        <!-- 頭像 + 基本資訊橫排 -->
        <div style="display:flex;align-items:center;gap:16px;margin-bottom:16px;padding-bottom:16px;border-bottom:1px solid var(--border-color,#30363d);">
          <div style="width:64px;height:64px;border-radius:50%;background:linear-gradient(135deg,#1f6feb,#58a6ff);display:flex;align-items:center;justify-content:center;font-size:22px;font-weight:700;color:#fff;flex-shrink:0;">${initials}</div>
          <div style="flex:1;min-width:0;">
            <div style="font-size:18px;font-weight:700;color:var(--text-primary,#e6edf3);margin-bottom:2px;">${user.displayName || user.username}</div>
            <div style="font-size:12px;color:var(--text-secondary,#8b949e);">@${user.username} · ${roleLabel[user.role] || user.role}</div>
            ${brokerName ? `<div style="font-size:11px;color:#58a6ff;margin-top:2px;">📌 ${brokerName}</div>` : ''}
            <div style="font-size:11px;color:var(--text-muted,#6e7681);margin-top:2px;">註冊：${new Date(user.createdAt).toLocaleDateString('zh-TW')}</div>
          </div>
        </div>

        <!-- 用戶名修改 -->
        <div style="margin-bottom:12px;">
          <label style="font-size:11px;color:var(--text-secondary,#8b949e);font-weight:500;display:block;margin-bottom:4px;">用戶名</label>
          <div style="display:flex;gap:8px;">
            <input type="text" id="username-input-trader" value="${user.username}"
              style="flex:1;padding:6px 10px;border:1px solid var(--border-color,#30363d);border-radius:6px;background:var(--bg-tertiary,#0d1117);color:var(--text-primary,#e6edf3);font-size:13px;"/>
            <button id="save-username-btn-trader"
              style="padding:6px 14px;background:#10a37f;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;white-space:nowrap;">修改</button>
          </div>
        </div>

        <!-- 郵箱 -->
        <div style="margin-bottom:12px;">
          <label style="font-size:11px;color:var(--text-secondary,#8b949e);font-weight:500;display:block;margin-bottom:4px;">郵箱</label>
          <div style="font-size:13px;color:var(--text-primary,#e6edf3);">${user.email || '未綁定'}</div>
        </div>

        <!-- 角色 -->
        <div style="margin-bottom:12px;">
          <label style="font-size:11px;color:var(--text-secondary,#8b949e);font-weight:500;display:block;margin-bottom:4px;">角色</label>
          <div style="display:flex;gap:8px;">
            <select id="user-role-select-trader"
              style="flex:1;padding:6px 10px;border:1px solid var(--border-color,#30363d);border-radius:6px;background:var(--bg-tertiary,#0d1117);color:var(--text-primary,#e6edf3);font-size:13px;">
              <option value="trader" ${user.role === 'trader' ? 'selected' : ''}>交易者</option>
              <option value="broker" ${user.role === 'broker' ? 'selected' : ''}>交易商</option>
            </select>
            <button id="save-role-btn-trader"
              style="padding:6px 14px;background:#10a37f;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;white-space:nowrap;">保存</button>
          </div>
        </div>

        <!-- 綁定狀態 -->
        <div style="margin-bottom:12px;">
          <label style="font-size:11px;color:var(--text-secondary,#8b949e);font-weight:500;display:block;margin-bottom:6px;">綁定狀態</label>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <div style="display:flex;align-items:center;gap:6px;padding:6px 12px;border:1px solid var(--border-color,#30363d);border-radius:20px;font-size:12px;background:var(--bg-tertiary,#0d1117);">
              <span>🦊 MetaMask</span>
              <span style="color:${user.walletAddress ? '#3fb950' : '#6e7681'};">${user.walletAddress ? '✓ 已綁定' : '未綁定'}</span>
            </div>
            <div style="display:flex;align-items:center;gap:6px;padding:6px 12px;border:1px solid var(--border-color,#30363d);border-radius:20px;font-size:12px;background:var(--bg-tertiary,#0d1117);">
              <span>🌐 Google</span>
              <span style="color:${user.googleId ? '#3fb950' : '#6e7681'};">${user.googleId ? '✓ 已綁定' : '未綁定'}</span>
            </div>
          </div>
          ${user.walletAddress ? `<div style="font-size:10px;color:var(--text-muted,#6e7681);margin-top:4px;word-break:break-all;">${user.walletAddress}</div>` : ''}
        </div>

        <!-- 注册时间 (隱藏，已移到頂部) -->
        
        <div style="display: flex; flex-direction: column; gap: 8px; grid-column: span 2;">
          <label style="font-size: 12px; color: #8b949e; font-weight: 500;">
            推荐人代码
            ${canEditReferrer 
              ? '<span style="color: #10a37f; font-size: 11px; margin-left: 8px;">✓ 48小时内可编辑</span>' 
              : '<span style="color: #8b949e; font-size: 11px; margin-left: 8px;">⏱ 已超过48小时</span>'
            }
          </label>
          <div style="display: flex; gap: 8px; align-items: center;">
            ${canEditReferrer 
              ? `
                <input 
                  type="text" 
                  id="referrer-id-input-trader" 
                  value="${user.usedReferralCode || ''}" 
                  placeholder="输入推荐码或UID"
                  style="flex: 1; padding: 6px 10px; border: 1px solid #30363d; border-radius: 6px; background: #0d1117; color: #d1d4dc; font-size: 13px;"
                />
                <button 
                  id="save-referrer-btn-trader"
                  style="padding: 6px 16px; background: #10a37f; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; white-space: nowrap;"
                >
                  保存
                </button>
              `
              : `
                <span>${user.usedReferralCode || '无'}</span>
                ${user.usedReferralCode 
                  ? `<button 
                      id="clear-referrer-btn-trader"
                      style="padding: 4px 12px; background: #f85149; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 12px; margin-left: 8px;"
                    >
                      解除
                    </button>`
                  : ''
                }
              `
            }
          </div>
        </div>
        
        ${(user as any).brokerInfo ? `
          <div style="display: flex; flex-direction: column; gap: 8px;">
            <label style="font-size: 12px; color: #8b949e; font-weight: 500;">所屬交易商</label>
            <span style="color: #58a6ff; font-size: 14px; font-weight: 500;">
              ${(user as any).brokerInfo.brokerUsername}
            </span>
          </div>
        ` : `
          <div style="display: flex; flex-direction: column; gap: 8px;">
            <label style="font-size: 12px; color: #8b949e; font-weight: 500;">所屬交易商</label>
            <span style="color: #8b949e; font-size: 13px;">未歸屬任何交易商</span>
          </div>
        `}

        ${user.referrerInfo ? `
          <div style="display: flex; flex-direction: column; gap: 8px;">
            <label style="font-size: 12px; color: #8b949e; font-weight: 500;">推荐人</label>
            <span style="color: #10a37f;">
              ${user.referrerInfo.displayName || user.referrerInfo.username}
            </span>
          </div>
          <div style="display: flex; flex-direction: column; gap: 8px;">
            <label style="font-size: 12px; color: #8b949e; font-weight: 500;">使用的推荐码</label>
            <span style="color: #10a37f; font-weight: 600;">
              ${user.usedReferralCode || '未知'}
            </span>
          </div>
        ` : ''}
      </div>
    `;

    // 绑定事件
    const saveUsernameBtn = document.getElementById('save-username-btn-trader');
    if (saveUsernameBtn) {
      saveUsernameBtn.addEventListener('click', async () => {
        const input = document.getElementById('username-input-trader') as HTMLInputElement;
        if (!input) return;
        
        const newUsername = input.value.trim();
        if (!newUsername) {
          alert('用户名不能为空');
          return;
        }

        if (newUsername === user.username) {
          alert('用户名未更改');
          return;
        }

        if (newUsername.length < 3 || newUsername.length > 20) {
          alert('用户名长度必须在3-20个字符之间');
          return;
        }

        if (!/^[a-zA-Z0-9_]+$/.test(newUsername)) {
          alert('用户名只能包含字母、数字和下划线');
          return;
        }

        try {
          await authApi.updateUsername(newUsername);
          alert('用户名修改成功');
          renderUserInfoToContainer(containerId); // 重新加载
        } catch (error: any) {
          alert(`修改失败: ${error.message}`);
        }
      });
    }

    const saveRoleBtn = document.getElementById('save-role-btn-trader');
    if (saveRoleBtn) {
      saveRoleBtn.addEventListener('click', async () => {
        const select = document.getElementById('user-role-select-trader') as HTMLSelectElement;
        if (!select) return;
        
        const newRole = select.value;
        if (newRole === user.role) {
          alert('角色未更改');
          return;
        }

        try {
          await authApi.updateRole(newRole as 'trader' | 'broker' | 'admin');
          alert('角色修改成功，请刷新页面');
          location.reload();
        } catch (error: any) {
          alert(`修改失败: ${error.message}`);
        }
      });
    }

    if (canEditReferrer) {
      const saveReferrerBtn = document.getElementById('save-referrer-btn-trader');
      if (saveReferrerBtn) {
        saveReferrerBtn.addEventListener('click', async () => {
          const input = document.getElementById('referrer-id-input-trader') as HTMLInputElement;
          if (!input) return;
          
          const referrerCode = input.value.trim();
          if (!referrerCode) {
            alert('请输入推荐码或UID');
            return;
          }

          try {
            await authApi.setReferrer(referrerCode);
            alert('推荐人代码已更新');
            renderUserInfoToContainer(containerId); // 重新加载
          } catch (error: any) {
            alert(`更新失败: ${error.message}`);
          }
        });
      }
    } else {
      const clearReferrerBtn = document.getElementById('clear-referrer-btn-trader');
      if (clearReferrerBtn) {
        clearReferrerBtn.addEventListener('click', async () => {
          if (!confirm('确定要解除推荐人关系吗？')) return;

          try {
            await authApi.setReferrer('');
            alert('推荐人关系已解除');
            renderUserInfoToContainer(containerId); // 重新加载
          } catch (error: any) {
            alert(`解除失败: ${error.message}`);
          }
        });
      }
    }

  } catch (error: any) {
    logger.error('渲染个人信息失败:', error);
    container.innerHTML = `<div style="color: #f85149; padding: 16px;">加载个人信息失败: ${error.message}</div>`;
  }
}

/**
 * 渲染交易设置到指定容器
 * @param containerId 容器ID
 */
export async function renderTradingSettingsToContainer(containerId: string): Promise<void> {
  const container = document.getElementById(containerId);
  if (!container) {
    logger.error(`容器 ${containerId} 不存在`);
    return;
  }

  try {
    // 获取交易设置
    const apiSettings = await settingsApi.getTradingSettings();
    if (!apiSettings) {
      throw new Error('无法获取交易设置');
    }

    // 获取交易商信息
    let brokerUid = 'SYSTEM';
    let brokerName = '系统默认';
    
    try {
      const brokerSettings = await settingsApi.getBrokerSettings();
      
      if (brokerSettings.brokerUid && brokerSettings.brokerUid !== 'SYSTEM') {
        const response = await fetch(`/api/auth/user/${brokerSettings.brokerUid}`, {
          headers: { 'Authorization': `Bearer ${authApi.getToken()}` }
        });
        if (response.ok) {
          const data = await response.json();
          brokerName = data.user?.username || brokerSettings.brokerUid.substring(0, 8);
        }
        brokerUid = brokerSettings.brokerUid;
      }
    } catch (error) {
      logger.warn('获取交易商信息失败，使用默认值', error);
    }

    const settings = {
      brokerUid,
      brokerName,
      assignedDate: Date.now(),
      updatedAt: Date.now(),
      limits: {
      },
      riskControl: {
        liquidationThreshold: apiSettings.liquidationThreshold,
      },
    };

    // 渲染HTML（完整表格布局，所有字段只读）
    container.innerHTML = `
      <div style="max-height: calc(100vh - 300px); overflow-y: auto; padding: 16px;">
        <!-- 基础信息 -->
        <div style="background: #0d1117; border: 1px solid #30363d; border-radius: 8px; padding: 12px; margin-bottom: 16px; display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
          <div style="font-size: 12px; color: #8b949e;">
            <strong>交易商名称:</strong> ${settings.brokerName}
          </div>
          <div style="font-size: 12px; color: #8b949e;">
            <strong>分配日期:</strong> ${new Date(settings.assignedDate).toLocaleDateString('zh-CN')}
          </div>
          <div style="font-size: 12px; color: #8b949e; grid-column: span 2;">
            <strong>最后更新:</strong> ${new Date(settings.updatedAt).toLocaleString('zh-CN')}
          </div>
        </div>

        <!-- 设置值表格（只读） -->
        <div style="overflow-x: auto;">
          <table style="width: 100%; border-collapse: collapse; background: #0d1117; border: 1px solid #30363d; border-radius: 8px;">
            <thead>
              <tr style="background: #161b22; border-bottom: 1px solid #30363d;">
                <th style="padding: 12px; text-align: left; font-size: 13px; color: #8b949e; width: 50%;">设置项</th>
                <th style="padding: 12px; text-align: left; font-size: 13px; color: #8b949e; width: 50%;">设定值</th>
              </tr>
            </thead>
            <tbody>
              <!-- 交易限制 -->

              <!-- 风控设置 -->
              <tr>
                <td style="padding: 12px; color: #c9d1d9; font-size: 13px;">
                  强平维持率阈值
                  <span style="color: #58a6ff; cursor: help; margin-left: 4px;" title="触发强制平仓的维持率阈值">ⓘ</span>
                </td>
                <td style="padding: 12px;">
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <input 
                      type="number" 
                      value="${settings.riskControl.liquidationThreshold}" 
                      disabled
                      style="flex: 1; padding: 6px 10px; background: #161b22; border: 1px solid #30363d; border-radius: 6px; color: #8b949e; font-size: 13px; cursor: not-allowed;"
                    >
                    <span style="color: #8b949e; font-size: 13px;">%</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 说明文字 -->
        <div style="margin-top: 16px; padding: 12px; background: rgba(88, 166, 255, 0.1); border: 1px solid rgba(88, 166, 255, 0.3); border-radius: 6px; font-size: 12px; color: #8b949e;">
          ℹ️ 交易设置由系统管理员统一配置，用户无法修改
        </div>
      </div>
    `;

  } catch (error: any) {
    logger.error('渲染交易设置失败:', error);
    container.innerHTML = `<div style="color: #f85149; padding: 16px;">加载交易设置失败: ${error.message}</div>`;
  }
}

/**
 * 渲染推荐系统到指定容器（完整内容，与用户管理页面一致）
 * @param containerId 容器ID
 */
export async function renderReferralSystemToContainer(containerId: string): Promise<void> {
  const container = document.getElementById(containerId);
  if (!container) {
    logger.error(`容器 ${containerId} 不存在`);
    return;
  }

  try {
    // 获取推荐码列表
    const codes = await referralApi.getUserReferralCodes();
    
    // 获取推荐统计
    const stats = await referralApi.getStats();
    
    // 获取被推荐用户
    const referredUsers = await referralApi.getReferralList();

    // 获取推荐奖励费率
    let referralRewardRate = 0;
    try {
      const brokerSettings = await settingsApi.getBrokerSettings();
      if (brokerSettings && typeof brokerSettings.referralDepositRewardRate === 'number') {
        referralRewardRate = brokerSettings.referralDepositRewardRate;
      }
    } catch (error) {
      logger.warn('获取推荐奖励费率失败:', error);
    }

    // 渲染完整HTML（方案A：緊湊表格式）
    container.innerHTML = `
      <div style="padding: 12px; width: 100%; height: 100%; overflow-y: auto; box-sizing: border-box; font-size: 13px;">

        <!-- ① 統計橫條 -->
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px; margin-bottom: 12px; background: #0d1117; border: 1px solid #30363d; border-radius: 8px; padding: 10px 12px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="color: #8b949e; font-size: 11px;">推薦人數</span>
            <span style="color: #58a6ff; font-weight: 700; font-size: 15px;">${stats?.totalReferrals || 0}</span>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="color: #8b949e; font-size: 11px;">活躍</span>
            <span style="color: #3fb950; font-weight: 700; font-size: 15px;">${stats?.activeReferrals || 0}</span>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="color: #8b949e; font-size: 11px;">累計獎勵</span>
            <span style="color: #10a37f; font-weight: 700; font-size: 15px;">${(stats?.totalRewards || 0).toFixed(2)} <span style="font-size: 10px; color: #6e7681;">USDT</span></span>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="color: #8b949e; font-size: 11px;">獎勵費率</span>
            <span style="color: #d29922; font-weight: 700; font-size: 15px;">${(referralRewardRate * 100).toFixed(2)}%</span>
          </div>
        </div>

        <!-- ② 推薦碼列表標題 -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
          <span style="color: #8b949e; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">推薦碼</span>
          <button id="create-referral-code-btn-trader" style="padding: 4px 10px; background: #10a37f; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 11px; font-weight: 600;">
            + 新增
          </button>
        </div>

        <!-- ③ 推薦碼緊湊表格 -->
        <div id="referral-codes-list-trader" style="background: #0d1117; border: 1px solid #30363d; border-radius: 8px; overflow: hidden; margin-bottom: 12px;">
          ${codes.length === 0
            ? `<div style="text-align: center; padding: 32px 12px; color: #8b949e; font-size: 12px;">
                 <div style="margin-bottom: 8px;"><svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#58a6ff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v10H4V12"/><path d="M22 7H2v5h20V7z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg></div>
                 <p style="margin: 4px 0;">尚無推薦碼，點擊「+ 新增」建立</p>
               </div>`
            : `<table style="width: 100%; border-collapse: collapse;">
                <thead style="background: #161b22;">
                  <tr>
                    <th style="padding: 7px 10px; text-align: left; font-size: 11px; color: #8b949e; border-bottom: 1px solid #30363d; font-weight: 600;">推薦碼</th>
                    <th style="padding: 7px 6px; text-align: right; font-size: 11px; color: #8b949e; border-bottom: 1px solid #30363d; font-weight: 600;">贈金池</th>
                    <th style="padding: 7px 6px; text-align: right; font-size: 11px; color: #8b949e; border-bottom: 1px solid #30363d; font-weight: 600;">次數</th>
                    <th style="padding: 7px 8px; text-align: right; font-size: 11px; color: #8b949e; border-bottom: 1px solid #30363d; font-weight: 600;">操作</th>
                  </tr>
                </thead>
                <tbody>
                  ${codes.map((code, idx) => {
                    const typeLabel = code.type === 'default' ? '默' : code.type === 'special' ? '特' : '自';
                    const typeColor = code.type === 'special' ? '#ffd700' : code.type === 'custom' ? '#58a6ff' : '#7d8590';
                    return `
                    <tr style="${idx < codes.length - 1 ? 'border-bottom: 1px solid #21262d;' : ''} opacity: ${code.isActive ? '1' : '0.5'};">
                      <td style="padding: 8px 10px;">
                        <div style="display: flex; align-items: center; gap: 6px;">
                          <span style="font-weight: 700; color: #58a6ff; letter-spacing: 0.5px; font-size: 13px;">${code.code}</span>
                          <span style="font-size: 10px; padding: 1px 5px; border-radius: 3px; background: rgba(255,255,255,0.06); color: ${typeColor};">${typeLabel}</span>
                          ${!code.isActive ? '<span style="font-size: 10px; color: #7d8590;">停用</span>' : ''}
                        </div>
                        <div style="font-size: 10px; color: #6e7681; margin-top: 2px;">${code.description || ''}</div>
                      </td>
                      <td style="padding: 8px 6px; text-align: right;">
                        <span style="font-weight: 600; color: #10a37f; font-size: 12px;">${(code.bonusPoolBalance || 0).toFixed(1)}</span>
                        <div style="font-size: 10px; color: #6e7681;">每次 ${(code.bonusPerUse || 0).toFixed(1)}</div>
                      </td>
                      <td style="padding: 8px 6px; text-align: right; color: #c9d1d9; font-size: 12px;">${code.usageCount || 0}</td>
                      <td style="padding: 8px 8px; text-align: right; white-space: nowrap;">
                        <button class="btn-deposit-pool" data-code-id="${code.id}" title="充值贈金池"
                          style="padding: 3px 7px; background: #10a37f; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 11px; margin-left: 3px;">
                          💰
                        </button>
                        <button class="btn-bonus-settings" data-code-id="${code.id}" title="設置"
                          style="padding: 3px 7px; background: #21262d; color: #c9d1d9; border: 1px solid #30363d; border-radius: 4px; cursor: pointer; font-size: 11px; margin-left: 3px;">
                          ⚙️
                        </button>
                        <button class="btn-code-details" data-code-id="${code.id}" title="詳情"
                          style="padding: 3px 7px; background: #21262d; color: #c9d1d9; border: 1px solid #30363d; border-radius: 4px; cursor: pointer; font-size: 11px; margin-left: 3px;">
                          ···
                        </button>
                      </td>
                    </tr>`;
                  }).join('')}
                </tbody>
              </table>`
          }
        </div>

        <!-- ④ 被推薦用戶（摺疊式標題 + 表格）-->
        <div style="margin-bottom: 4px;">
          <span style="color: #8b949e; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">
            被推薦用戶（${referredUsers.length}）
          </span>
        </div>
        <div style="background: #0d1117; border: 1px solid #30363d; border-radius: 8px; overflow: hidden;">
          ${referredUsers.length === 0
            ? `<div style="text-align: center; padding: 20px 12px; color: #6e7681; font-size: 12px;">暫無被推薦用戶，分享推薦碼邀請好友</div>`
            : `<table style="width: 100%; border-collapse: collapse;">
                <thead style="background: #161b22;">
                  <tr>
                    <th style="padding: 7px 10px; text-align: left; font-size: 11px; color: #8b949e; border-bottom: 1px solid #30363d; font-weight: 600;">用戶名</th>
                    <th style="padding: 7px 8px; text-align: left; font-size: 11px; color: #8b949e; border-bottom: 1px solid #30363d; font-weight: 600;">時間</th>
                    <th style="padding: 7px 8px; text-align: center; font-size: 11px; color: #8b949e; border-bottom: 1px solid #30363d; font-weight: 600;">狀態</th>
                  </tr>
                </thead>
                <tbody>
                  ${referredUsers.map((user, index) => `
                    <tr style="${index < referredUsers.length - 1 ? 'border-bottom: 1px solid #21262d;' : ''}">
                      <td style="padding: 7px 10px; color: #c9d1d9; font-size: 12px;">${user.username}</td>
                      <td style="padding: 7px 8px; color: #8b949e; font-size: 11px;">${new Date(user.createdAt).toLocaleDateString('zh-TW')}</td>
                      <td style="padding: 7px 8px; text-align: center;">
                        <span style="font-size: 11px; color: ${user.status === 'active' ? '#3fb950' : '#7d8590'};">
                          ${user.status === 'active' ? '✓' : '○'}
                        </span>
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>`
          }
        </div>

      </div>
    `;

    // 绑定创建推荐码按钮事件
    const createBtn = document.getElementById('create-referral-code-btn-trader');
    if (createBtn) {
      createBtn.addEventListener('click', () => {
        // 显示浮动模态框
        const modal = document.getElementById('create-referral-code-modal-trader');
        if (modal) {
          modal.style.display = 'flex';
          
          // 清空输入
          const codeInput = modal.querySelector('#new-code-input-trader') as HTMLInputElement;
          const descInput = modal.querySelector('#new-code-description-trader') as HTMLInputElement;
          const expiresInput = modal.querySelector('#new-code-expires-trader') as HTMLInputElement;
          
          if (codeInput) codeInput.value = '';
          if (descInput) descInput.value = '';
          if (expiresInput) expiresInput.value = '';
        }
      });
    }
    
    // 绑定浮动模态框的关闭按钮
    const closeModalBtn = document.querySelector('#create-referral-code-modal-trader .close-modal-btn');
    if (closeModalBtn) {
      closeModalBtn.addEventListener('click', () => {
        const modal = document.getElementById('create-referral-code-modal-trader');
        if (modal) modal.style.display = 'none';
      });
    }
    
    // 绑定浮动模态框的取消按钮
    const cancelModalBtn = document.querySelector('#create-referral-code-modal-trader .cancel-modal-btn');
    if (cancelModalBtn) {
      cancelModalBtn.addEventListener('click', () => {
        const modal = document.getElementById('create-referral-code-modal-trader');
        if (modal) modal.style.display = 'none';
      });
    }
    
    // 绑定浮动模态框的确认按钮
    const confirmModalBtn = document.querySelector('#create-referral-code-modal-trader .confirm-modal-btn');
    if (confirmModalBtn) {
      confirmModalBtn.addEventListener('click', async () => {
        const modal = document.getElementById('create-referral-code-modal-trader');
        const codeInput = modal?.querySelector('#new-code-input-trader') as HTMLInputElement;
        const descInput = modal?.querySelector('#new-code-description-trader') as HTMLInputElement;
        const expiresInput = modal?.querySelector('#new-code-expires-trader') as HTMLInputElement;
        
        const code = codeInput?.value.trim().toUpperCase();
        if (!code) {
          alert('❌ 请输入推荐码');
          return;
        }
        
        if (!/^[A-Z0-9]{6,12}$/.test(code)) {
          alert('❌ 推荐码必须是6-12位大写字母或数字');
          return;
        }

        const description = descInput?.value || '';
        const expiresAt = expiresInput?.value ? new Date(expiresInput.value) : undefined;

        try {
          const result = await referralApi.createReferralCode(code, description, expiresAt);
          alert(result.message || '✅ 创建成功！');
          
          // 关闭模态框
          if (modal) modal.style.display = 'none';
          
          // 重新渲染
          await renderReferralSystemToContainer(containerId);
        } catch (error: any) {
          alert(`❌ 创建失败：${error.message}`);
        }
      });
    }


    // ========== 使用事件委托方式绑定所有按钮（更可靠） ==========
    
    // 移除旧的事件监听器（如果有）
    const oldHandler = (container as any)._clickHandler;
    if (oldHandler) {
      container.removeEventListener('click', oldHandler);
    }

    // 创建统一的点击事件处理器
    const clickHandler = async (e: Event) => {
      const target = e.target as HTMLElement;
      
      // 查找实际的按钮元素（可能点击到了按钮内的文本或图标）
      const button = target.closest('button');
      if (!button) return;

      const codeId = button.getAttribute('data-code-id');
      if (!codeId) return;

      const code = codes.find(c => c.id === codeId);
      if (!code) {
        alert('❌ 推荐码不存在');
        return;
      }

      // 根据按钮类名执行不同操作
      if (button.classList.contains('btn-deposit-pool')) {
        // 充值按钮
        logger.info(`[推荐系统] 充值按钮被点击，codeId: ${codeId}`);
        await handleDepositClick(code, codeId, containerId);
      } else if (button.classList.contains('btn-bonus-settings')) {
        // 设置按钮
        logger.info(`[推荐系统] 设置按钮被点击，codeId: ${codeId}`);
        await handleSettingsClick(code, codeId, containerId);
      } else if (button.classList.contains('btn-code-details')) {
        // 详情按钮
        logger.info(`[推荐系统] 详情按钮被点击，codeId: ${codeId}`);
        await handleDetailsClick(code, codeId, containerId);
      }
    };

    // 绑定到容器
    container.addEventListener('click', clickHandler);
    (container as any)._clickHandler = clickHandler;
    logger.info(`[推荐系统] 事件委托已绑定到容器`);

    // ========== 辅助函数：处理充值按钮点击 ==========
    async function handleDepositClick(code: any, codeId: string, containerId: string) {

        // 显示充值模态框
        const modal = document.getElementById('deposit-bonus-pool-modal-trader');
        if (!modal) return;

        // 填充基本信息
        const codeEl = document.getElementById('deposit-pool-code');
        const currentBalanceEl = document.getElementById('deposit-pool-current-balance');
        const amountInput = document.getElementById('deposit-pool-amount') as HTMLInputElement;
        const summaryBox = document.getElementById('deposit-pool-summary');
        
        if (codeEl) codeEl.textContent = code.code;
        if (currentBalanceEl) currentBalanceEl.textContent = `${(code.bonusPoolBalance || 0).toFixed(2)} USDT`;
        if (amountInput) amountInput.value = '';
        if (summaryBox) summaryBox.style.display = 'none';

        // 获取账户余额
        try {
          const account = await accountApi.getAccount();
          const accountBalanceEl = document.getElementById('deposit-pool-account-balance');
          if (accountBalanceEl) accountBalanceEl.textContent = `${account!.availableBalance.toFixed(2)} USDT`;
        } catch (error) {
          logger.error('获取账户余额失败:', error);
        }

        modal.style.display = 'flex';

        // 监听金额输入变化
        const handleAmountInput = () => {
          const amount = parseFloat(amountInput?.value || '0');
          if (amount > 0 && summaryBox) {
            const newBalance = (code.bonusPoolBalance || 0) + amount;
            const estimatedUses = code.bonusPerUse > 0 ? Math.floor(newBalance / code.bonusPerUse) : 0;
            
            const newBalanceEl = document.getElementById('deposit-pool-new-balance');
            const estimatedUsesEl = document.getElementById('deposit-pool-estimated-uses');
            
            if (newBalanceEl) newBalanceEl.textContent = `${newBalance.toFixed(2)} USDT`;
            if (estimatedUsesEl) estimatedUsesEl.textContent = String(estimatedUses);
            
            summaryBox.style.display = 'block';
          } else if (summaryBox) {
            summaryBox.style.display = 'none';
          }
        };
        
        if (amountInput) {
          amountInput.addEventListener('input', handleAmountInput);
        }

        // 绑定取消按钮
        const cancelBtn = document.getElementById('deposit-pool-cancel');
        const handleCancel = () => {
          modal.style.display = 'none';
          if (amountInput) amountInput.removeEventListener('input', handleAmountInput);
        };
        if (cancelBtn) {
          cancelBtn.onclick = handleCancel;
        }

        // 绑定确认按钮
        const confirmBtn = document.getElementById('deposit-pool-confirm');
        const handleConfirm = async () => {
          const amount = parseFloat(amountInput?.value || '0');

          if (isNaN(amount) || amount <= 0) {
            alert('❌ 请输入有效的充值金额');
            return;
          }

          try {
            await referralApi.depositToBonusPool(codeId, amount);
            alert(`✅ 充值成功！已向 ${code.code} 充值 ${amount.toFixed(2)} USDT`);
            
            modal.style.display = 'none';
            if (amountInput) amountInput.removeEventListener('input', handleAmountInput);
            await renderReferralSystemToContainer(containerId);
          } catch (error: any) {
            alert(`❌ 充值失败：${error.message}`);
          }
        };
        if (confirmBtn) {
          confirmBtn.onclick = handleConfirm;
        }

        // 绑定模态框关闭按钮
        const closeBtn = modal.querySelector('.close-modal-btn');
        const handleClose = () => {
          modal.style.display = 'none';
          if (amountInput) amountInput.removeEventListener('input', handleAmountInput);
        };
        if (closeBtn) {
          (closeBtn as HTMLElement).onclick = handleClose;
        }
    }

    // ========== 辅助函数：处理设置按钮点击 ==========
    async function handleSettingsClick(code: any, codeId: string, containerId: string) {

        // 显示设置模态框
        const modal = document.getElementById('bonus-settings-modal-trader');
        if (!modal) return;

        // 填充基本信息
        const codeEl = document.getElementById('settings-code');
        const poolBalanceEl = document.getElementById('settings-pool-balance');
        const currentBonusEl = document.getElementById('settings-current-bonus');
        const amountInput = document.getElementById('settings-bonus-amount') as HTMLInputElement;
        const summaryBox = document.getElementById('settings-summary');
        
        if (codeEl) codeEl.textContent = code.code;
        if (poolBalanceEl) poolBalanceEl.textContent = `${(code.bonusPoolBalance || 0).toFixed(2)} USDT`;
        if (currentBonusEl) currentBonusEl.textContent = `${(code.bonusPerUse || 0).toFixed(2)} USDT`;
        if (amountInput) amountInput.value = (code.bonusPerUse || 0).toFixed(2);
        if (summaryBox) summaryBox.style.display = 'none';

        modal.style.display = 'flex';

        // 监听金额输入变化
        const handleAmountInput = () => {
          const bonusAmount = parseFloat(amountInput?.value || '0');
          if (bonusAmount > 0 && summaryBox) {
            const estimatedUses = Math.floor((code.bonusPoolBalance || 0) / bonusAmount);
            
            const estimatedUsesEl = document.getElementById('settings-estimated-uses');
            if (estimatedUsesEl) estimatedUsesEl.textContent = String(estimatedUses);
            
            summaryBox.style.display = 'block';
          } else if (summaryBox) {
            summaryBox.style.display = 'none';
          }
        };
        
        if (amountInput) {
          amountInput.addEventListener('input', handleAmountInput);
          handleAmountInput(); // 初始化显示
        }

        // 绑定取消按钮
        const cancelBtn = document.getElementById('settings-cancel');
        const handleCancel = () => {
          modal.style.display = 'none';
          if (amountInput) amountInput.removeEventListener('input', handleAmountInput);
        };
        if (cancelBtn) {
          cancelBtn.onclick = handleCancel;
        }

        // 绑定确认按钮
        const confirmBtn = document.getElementById('settings-confirm');
        const handleConfirm = async () => {
          const bonusNum = parseFloat(amountInput?.value || '0');

          if (isNaN(bonusNum) || bonusNum < 0) {
            alert('❌ 请输入有效的金额');
            return;
          }

          try {
            await referralApi.setBonusPerUse(codeId, bonusNum);
            alert(`✅ 设置成功！${code.code} 的每次赠金已设置为 ${bonusNum.toFixed(2)} USDT`);
            
            modal.style.display = 'none';
            if (amountInput) amountInput.removeEventListener('input', handleAmountInput);
            await renderReferralSystemToContainer(containerId);
          } catch (error: any) {
            alert(`❌ 设置失败：${error.message}`);
          }
        };
        if (confirmBtn) {
          confirmBtn.onclick = handleConfirm;
        }

        // 绑定模态框关闭按钮
        const closeBtn = modal.querySelector('.close-modal-btn');
        const handleClose = () => {
          modal.style.display = 'none';
          if (amountInput) amountInput.removeEventListener('input', handleAmountInput);
        };
        if (closeBtn) {
          (closeBtn as HTMLElement).onclick = handleClose;
        }
    }

    // ========== 辅助函数：处理详情按钮点击 ==========
    async function handleDetailsClick(code: any, codeId: string, containerId: string) {
        logger.info(`[推荐系统] 🔵 handleDetailsClick 被调用! code: ${code.code}, codeId: ${codeId}`);

        try {
          logger.info(`[推荐系统] 🔄 开始获取交易记录...`);
          const transactions = await referralApi.getBonusTransactions(codeId);
          logger.info(`[推荐系统] ✅ 获取到 ${transactions.length} 条交易记录`);
          
          // 显示详情模态框
          logger.info(`[推荐系统] 🔍 查找模态框元素: referral-code-detail-modal-trader`);
          const modal = document.getElementById('referral-code-detail-modal-trader');
          if (!modal) {
            alert('❌ 模态框元素未找到');
            logger.error('[推荐系统] ❌ 模态框元素 referral-code-detail-modal-trader 未找到');
            return;
          }

          logger.info('[推荐系统] ✅ 找到模态框元素，开始填充数据...');

          // 填充基本信息
          const detailCode = document.getElementById('detail-code');
          const detailType = document.getElementById('detail-type');
          const detailStatus = document.getElementById('detail-status');
          const detailUsageCount = document.getElementById('detail-usage-count');
          const detailDescription = document.getElementById('detail-description') as HTMLInputElement;

          if (detailCode) detailCode.textContent = code.code;
          if (detailType) {
            const typeLabels: Record<string, string> = {
              default: '默认',
              special: '特殊',
              custom: '自定义'
            };
            detailType.textContent = typeLabels[code.type] || code.type;
            detailType.className = `type-badge ${code.type}`;
          }
          if (detailStatus) {
            detailStatus.textContent = code.isActive ? '启用' : '禁用';
            detailStatus.className = `status-badge ${code.isActive ? 'active' : 'inactive'}`;
            detailStatus.style.padding = '4px 8px';
            detailStatus.style.borderRadius = '4px';
            detailStatus.style.fontSize = '11px';
            detailStatus.style.fontWeight = '600';
            detailStatus.style.display = 'inline-block';
            detailStatus.style.background = code.isActive ? 'rgba(63, 185, 80, 0.1)' : 'rgba(139, 148, 158, 0.1)';
            detailStatus.style.color = code.isActive ? '#3fb950' : '#8b949e';
          }
          if (detailUsageCount) detailUsageCount.textContent = code.usageCount.toString();
          if (detailDescription) detailDescription.value = code.description || '';

          // 填充赠金池状态
          const detailBalance = document.getElementById('detail-balance');
          const detailUserDeposit = document.getElementById('detail-user-deposit');
          const detailPlatformBonus = document.getElementById('detail-platform-bonus');
          const detailTotalGiven = document.getElementById('detail-total-given');
          const detailBonusPerUse = document.getElementById('detail-bonus-per-use');
          const detailRemainingUses = document.getElementById('detail-remaining-uses');

          if (detailBalance) detailBalance.textContent = `${code.bonusPoolBalance.toFixed(2)} USDT`;
          if (detailUserDeposit) detailUserDeposit.textContent = `${code.userDepositAmount.toFixed(2)} USDT`;
          if (detailPlatformBonus) detailPlatformBonus.textContent = `${code.platformBonusAmount.toFixed(2)} USDT`;
          if (detailTotalGiven) detailTotalGiven.textContent = `${code.totalBonusGiven.toFixed(2)} USDT`;
          if (detailBonusPerUse) detailBonusPerUse.textContent = `${code.bonusPerUse.toFixed(2)} USDT`;
          
          const remainingUses = code.bonusPerUse > 0 ? Math.floor(code.bonusPoolBalance / code.bonusPerUse) : 0;
          if (detailRemainingUses) detailRemainingUses.textContent = remainingUses.toString();

          // 填充交易记录
          const tbody = document.querySelector('#detail-transactions tbody');
          if (tbody) {
            if (transactions.length === 0) {
              tbody.innerHTML = `
                <tr>
                  <td colspan="5" style="text-align: center; padding: 20px; color: #8b949e;">暂无交易记录</td>
                </tr>
              `;
            } else {
              const formatDate = (dateString: string) => {
                const date = new Date(dateString);
                return date.toLocaleString('zh-CN', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                });
              };

              const getTransactionTypeLabel = (type: string) => {
                const labels: Record<string, string> = {
                  user_deposit: '💰 用户充值',
                  platform_grant: '🎁 平台赠金',
                  bonus_given: '📤 赠金发放',
                };
                return labels[type] || type;
              };

              tbody.innerHTML = transactions.map((tx: any) => `
                <tr style="border-bottom: 1px solid #21262d;">
                  <td style="padding: 10px 12px; color: #8b949e;">${formatDate(tx.createdAt)}</td>
                  <td style="padding: 10px 12px;">${getTransactionTypeLabel(tx.transactionType)}</td>
                  <td style="padding: 10px 12px; text-align: right; color: ${tx.amount >= 0 ? '#3fb950' : '#f85149'}; font-weight: 600;">
                    ${tx.amount >= 0 ? '+' : ''}${tx.amount.toFixed(2)} USDT
                  </td>
                  <td style="padding: 10px 12px; text-align: center; color: #c9d1d9;">
                    ${tx.balanceBefore.toFixed(2)} → ${tx.balanceAfter.toFixed(2)}
                  </td>
                  <td style="padding: 10px 12px; color: #8b949e;">${tx.description || '-'}</td>
                </tr>
              `).join('');
            }
          }

          // 绑定复制推荐码按钮
          const copyCodeBtn = document.getElementById('detail-copy-code');
          if (copyCodeBtn) {
            copyCodeBtn.onclick = () => {
              navigator.clipboard.writeText(code.code).then(() => {
                alert('✅ 推荐码已复制到剪贴板');
              }).catch(() => {
                alert('❌ 复制失败，请手动复制');
              });
            };
          }

          // 绑定保存备注按钮
          const saveDescBtn = document.getElementById('save-description');
          if (saveDescBtn) {
            saveDescBtn.onclick = async () => {
              const newDesc = detailDescription?.value || '';
              try {
                await referralApi.updateReferralCodeDescription(codeId, newDesc);
                alert('✅ 备注保存成功');
                code.description = newDesc;
                await renderReferralSystemToContainer(containerId);
              } catch (error: any) {
                alert(`❌ 保存失败：${error.message}`);
              }
            };
          }

          // 绑定充值按钮
          const depositBtn = document.getElementById('detail-deposit-btn');
          if (depositBtn) {
            depositBtn.onclick = () => {
              modal.style.display = 'none';
              const depositModal = document.getElementById('deposit-bonus-pool-modal-trader');
              if (depositModal) {
                // 填充充值模态框数据
                const depositCode = document.getElementById('deposit-code');
                if (depositCode) depositCode.textContent = code.code;
                depositModal.style.display = 'flex';
                
                // 绑定充值确认按钮（需要额外实现）
                const confirmDepositBtn = document.getElementById('confirm-deposit');
                if (confirmDepositBtn) {
                  confirmDepositBtn.onclick = async () => {
                    const amountInput = document.getElementById('deposit-amount-input') as HTMLInputElement;
                    const amount = parseFloat(amountInput?.value || '0');
                    if (isNaN(amount) || amount <= 0) {
                      alert('❌ 请输入有效金额');
                      return;
                    }
                    
                    try {
                      await referralApi.depositToBonusPool(codeId, amount);
                      alert(`✅ 充值成功！已向 ${code.code} 充值 ${amount} USDT`);
                      depositModal.style.display = 'none';
                      await renderReferralSystemToContainer(containerId);
                    } catch (error: any) {
                      alert(`❌ 充值失败：${error.message}`);
                    }
                  };
                }
              }
            };
          }

          // 绑定设置赠金按钮
          const setBonusBtn = document.getElementById('detail-set-bonus-btn');
          if (setBonusBtn) {
            setBonusBtn.onclick = () => {
              modal.style.display = 'none';
              const settingsModal = document.getElementById('bonus-settings-modal-trader');
              if (settingsModal) {
                // 填充设置模态框数据
                const settingsCode = document.getElementById('settings-code');
                const currentBonusDisplay = document.getElementById('current-bonus-display');
                if (settingsCode) settingsCode.textContent = code.code;
                if (currentBonusDisplay) currentBonusDisplay.textContent = `${code.bonusPerUse.toFixed(2)} USDT`;
                settingsModal.style.display = 'flex';
                
                // 已有绑定逻辑，无需重复
              }
            };
          }

          // 绑定平台赠金按钮（仅Admin可见）
          const platformGrantBtn = document.getElementById('detail-platform-grant-btn');
          const currentSession = authApi.getSession();
          if (platformGrantBtn && currentSession?.role === 'admin') {
            platformGrantBtn.style.display = 'inline-block';
            platformGrantBtn.onclick = async () => {
              const amountStr = prompt('请输入平台赠金金额 (USDT):');
              if (!amountStr) return;
              
              const amount = parseFloat(amountStr);
              if (isNaN(amount) || amount <= 0) {
                alert('❌ 请输入有效的金额');
                return;
              }
              
              const description = prompt('备注说明（可选）:', `平台赠金 ${amount} USDT`);
              
              // TODO: 添加二次确认机制（管理员密码验证）
              // const confirmPassword = prompt('请输入管理员密码确认操作:');
              // if (!confirmPassword) return;
              
              try {
                const response = await fetch(`/api/referral/codes/${codeId}/platform-grant`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${authApi.getToken()}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ amount, description }),
                });
                
                if (!response.ok) {
                  const error = await response.json();
                  throw new Error(error.error || '平台赠金失败');
                }
                
                alert(`✅ 平台赠金成功！已向推荐码 ${code.code} 的赠金池发放 ${amount} USDT`);
                
                // 刷新详情
                modal.style.display = 'none';
                await renderReferralSystemToContainer(containerId);
              } catch (error: any) {
                alert(`❌ 平台赠金失败：${error.message}`);
              }
            };
          }

          // 绑定切换状态按钮
          const toggleStatusBtn = document.getElementById('detail-toggle-status');
          if (toggleStatusBtn) {
            toggleStatusBtn.onclick = async () => {
              try {
                await referralApi.toggleReferralCode(codeId);
                alert(`✅ 推荐码已${code.isActive ? '禁用' : '启用'}`);
                modal.style.display = 'none';
                await renderReferralSystemToContainer(containerId);
              } catch (error: any) {
                alert(`❌ 操作失败：${error.message}`);
              }
            };
          }

          // 绑定删除按钮
          const deleteBtn = document.getElementById('detail-delete-code');
          if (deleteBtn) {
            deleteBtn.onclick = async () => {
              if (!confirm(`确定要删除推荐码 ${code.code} 吗？此操作不可恢复。`)) return;
              try {
                await referralApi.deleteReferralCode(codeId);
                alert('✅ 推荐码已删除');
                modal.style.display = 'none';
                await renderReferralSystemToContainer(containerId);
              } catch (error: any) {
                alert(`❌ 删除失败：${error.message}`);
              }
            };
          }

          // 绑定关闭按钮
          const closeBtn = document.getElementById('detail-close-btn');
          const modalCloseBtn = modal.querySelector('.close-modal-btn');
          const handleClose = () => {
            modal.style.display = 'none';
          };
          if (closeBtn) closeBtn.onclick = handleClose;
          if (modalCloseBtn) (modalCloseBtn as HTMLElement).onclick = handleClose;

          // 显示模态框
          logger.info('[推荐系统] 🎉 准备显示模态框...');
          modal.style.display = 'flex';
          logger.info('[推荐系统] ✅ 模态框已设置为 display: flex');

        } catch (error: any) {
          logger.error('[推荐系统] ❌ 加载详情失败:', error);
          alert(`❌ 加载详情失败：${error.message}`);
        }
    }

  } catch (error: any) {
    logger.error('渲染推荐系统失败:', error);
    container.innerHTML = `
      <div style="text-align: center; padding: 48px 20px; color: #f85149;">
        <div style="font-size: 48px; margin-bottom: 16px;">❌</div>
        <p style="margin: 8px 0; font-size: 14px;">加载推荐系统失败</p>
        <p style="margin: 8px 0; font-size: 13px; color: #8b949e;">${error.message}</p>
      </div>
    `;
  }
}

